import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Order Number Management Component
export const OrderNumberManagement = () => {
  const theme = useThemeClasses();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    prefix: 'ORD',
    next_number: 1,
    suffix: '',
    reset_monthly: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/order-number-settings`);
      setSettings(response.data);
      setFormData({
        prefix: response.data.prefix,
        next_number: response.data.next_number,
        suffix: response.data.suffix,
        reset_monthly: response.data.reset_monthly
      });
    } catch (error) {
      console.error('Failed to load order number settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await axios.put(`${API}/admin/order-number-settings`, formData);
      setSettings(response.data);
      toast.success('Order number settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the order numbering? This will affect future orders.')) return;

    try {
      const startNumber = parseInt(prompt('Enter starting number:', '1'));
      if (isNaN(startNumber) || startNumber < 1) {
        toast.error('Please enter a valid number');
        return;
      }

      await axios.post(`${API}/admin/orders/reset-numbering`, {
        start_number: startNumber
      });
      
      toast.success(`Order numbering reset to start from ${startNumber}`);
      loadSettings();
    } catch (error) {
      console.error('Failed to reset order numbering:', error);
      toast.error('Failed to reset order numbering');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading order number settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Order Number Management</h2>
      </div>

      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Order Number Format</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Prefix</label>
              <input
                type="text"
                value={formData.prefix}
                onChange={(e) => setFormData({...formData, prefix: e.target.value.toUpperCase()})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="ORD"
                maxLength="10"
              />
              <p className={`text-sm ${theme.textSecondary} mt-1`}>e.g., ORD, RG3D, INV</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Next Number</label>
              <input
                type="number"
                value={formData.next_number}
                onChange={(e) => setFormData({...formData, next_number: parseInt(e.target.value)})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                min="1"
              />
              <p className={`text-sm ${theme.textSecondary} mt-1`}>Next order will be #{formData.next_number}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Suffix (Optional)</label>
              <input
                type="text"
                value={formData.suffix}
                onChange={(e) => setFormData({...formData, suffix: e.target.value.toUpperCase()})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="CA, 2025"
                maxLength="10"
              />
              <p className={`text-sm ${theme.textSecondary} mt-1`}>e.g., CA, 2025 (optional)</p>
            </div>
            <div className="flex items-center space-x-2 mt-6">
              <input
                type="checkbox"
                id="reset_monthly"
                checked={formData.reset_monthly}
                onChange={(e) => setFormData({...formData, reset_monthly: e.target.checked})}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="reset_monthly" className="text-sm font-medium">
                Reset numbering monthly
              </label>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Preview:</h4>
            <p className="text-blue-700">
              Next order number will be: <strong>
                {formData.suffix ? 
                  `${formData.prefix}-${String(formData.next_number).padStart(6, '0')}-${formData.suffix}` :
                  `${formData.prefix}-${String(formData.next_number).padStart(6, '0')}`
                }
              </strong>
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700"
            >
              Reset Numbering
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Enhanced Analytics Dashboard
export const AnalyticsDashboard = () => {
  const theme = useThemeClasses();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/dashboard`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleResetVisitors = () => {
    setShowResetConfirm(true);
  };

  const confirmResetVisitors = async () => {
    setResetting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(`${API}/admin/analytics/reset-visitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || 'Visitor tracking reset successfully');
      setShowResetConfirm(false);
      // Reload analytics to show updated data
      loadAnalytics();
    } catch (error) {
      console.error('Failed to reset visitor tracking:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset visitor tracking');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading analytics...</div>;
  }

  if (!analytics) {
    return <div className={`text-center p-8 ${theme.textSecondary}`}>No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
      
      {/* Visitor Analytics */}
      {analytics.visitor_analytics && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">👥 Visitor Tracking</h3>
            <button
              onClick={handleResetVisitors}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm transition-colors"
            >
              🔄 Reset Visitor Data
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-cyan-900/30' : 'bg-cyan-50'} border ${theme.isDark ? 'border-cyan-700' : 'border-cyan-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>Total Visitors</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                {analytics.visitor_analytics.total_visitors}
              </p>
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                {analytics.visitor_analytics.unique_visitors_total} unique
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-blue-900/30' : 'bg-blue-50'} border ${theme.isDark ? 'border-blue-700' : 'border-blue-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>Today</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                {analytics.visitor_analytics.today_visitors}
              </p>
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                {analytics.visitor_analytics.unique_visitors_today} unique
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-indigo-900/30' : 'bg-indigo-50'} border ${theme.isDark ? 'border-indigo-700' : 'border-indigo-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>This Week</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                {analytics.visitor_analytics.week_visitors}
              </p>
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                {analytics.visitor_analytics.unique_visitors_week} unique
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-green-900/30' : 'bg-green-50'} border ${theme.isDark ? 'border-green-700' : 'border-green-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Conversion Today</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-green-300' : 'text-green-700'}`}>
                {analytics.visitor_analytics.conversion_rate_today}%
              </p>
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                Visitors → Orders
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'} border ${theme.isDark ? 'border-emerald-700' : 'border-emerald-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Avg Conversion</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                {analytics.visitor_analytics.conversion_rate_total}%
              </p>
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                All time
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Financial Summary Integration */}
      {analytics.financial_summary && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-xl font-bold mb-4">💰 Financial Quick View</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-amber-900/30' : 'bg-amber-50'} border ${theme.isDark ? 'border-amber-700' : 'border-amber-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-amber-400' : 'text-amber-600'}`}>Inventory Value</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                ${analytics.financial_summary.inventory_value.toFixed(2)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-red-900/30' : 'bg-red-50'} border ${theme.isDark ? 'border-red-700' : 'border-red-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-red-400' : 'text-red-600'}`}>Total Costs</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-red-300' : 'text-red-700'}`}>
                ${analytics.financial_summary.total_costs.toFixed(2)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-green-900/30' : 'bg-green-50'} border ${theme.isDark ? 'border-green-700' : 'border-green-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Gross Profit</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-green-300' : 'text-green-700'}`}>
                ${analytics.financial_summary.gross_profit.toFixed(2)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'} border ${theme.isDark ? 'border-emerald-700' : 'border-emerald-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Net Profit</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                ${analytics.financial_summary.net_profit.toFixed(2)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-purple-900/30' : 'bg-purple-50'} border ${theme.isDark ? 'border-purple-700' : 'border-purple-200'}`}>
              <p className={`text-sm font-medium ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>Profit Margin</p>
              <p className={`text-2xl font-bold mt-1 ${theme.isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                {analytics.financial_summary.profit_margin}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-blue-500`}>
          <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>Total Orders</h3>
          <p className="text-3xl font-bold text-blue-600">{analytics.total_orders}</p>
          <p className={`text-sm ${theme.textSecondary}`}>All time</p>
        </div>
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-green-500`}>
          <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>Total Revenue</h3>
          <p className="text-3xl font-bold text-green-600">${analytics.total_revenue.toFixed(2)}</p>
          <p className={`text-sm ${theme.textSecondary}`}>All time</p>
        </div>
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-purple-500`}>
          <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>Today's Orders</h3>
          <p className="text-3xl font-bold text-purple-600">{analytics.today_orders}</p>
          <p className={`text-sm ${theme.textSecondary}`}>${analytics.today_revenue.toFixed(2)} revenue</p>
        </div>
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-orange-500`}>
          <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>This Week</h3>
          <p className="text-3xl font-bold text-orange-600">{analytics.week_orders}</p>
          <p className={`text-sm ${theme.textSecondary}`}>${analytics.week_revenue.toFixed(2)} revenue</p>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Order Status Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(analytics.status_breakdown).map(([status, count]) => (
              <div key={status} className="flex justify-between items-center">
                <span className="capitalize font-medium">{status.replace('_', ' ')}</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  status === 'delivered' ? 'bg-green-100 text-green-800' :
                  status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                  status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Selling Products */}
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Top Selling Products</h3>
          <div className="space-y-3">
            {analytics.top_products && analytics.top_products.length > 0 ? (
              analytics.top_products.map((product, index) => (
                <div key={product?.product_id || index} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{product?.name || 'Unknown Product'}</p>
                    <p className={`text-sm ${theme.textSecondary}`}>{product?.total_sold || 0} sold</p>
                  </div>
                  <span className="font-bold text-green-600">${product?.revenue?.toFixed(2) || '0.00'}</span>
                </div>
              ))
            ) : (
              <p className={`text-sm ${theme.textSecondary}`}>No products data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Recent Orders</h3>
          <div className="space-y-3">
            {analytics.recent_orders && analytics.recent_orders.length > 0 ? (
              analytics.recent_orders.map((order) => (
                <div key={order?.id || Math.random()} className="border-b pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{order?.order_number || `#${order?.id?.slice(-8) || 'N/A'}`}</p>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        {order?.customer_info?.name || `${order?.customer_info?.first_name || ''} ${order?.customer_info?.last_name || ''}`.trim() || order?.customer_email || 'N/A'}
                      </p>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        {order?.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${order?.total?.toFixed(2) || '0.00'}</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order?.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order?.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order?.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order?.order_status || 'unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className={`text-sm ${theme.textSecondary}`}>No recent orders available</p>
            )}
          </div>
        </div>

        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Inventory Alerts</h3>
          {analytics.low_stock_products.length > 0 ? (
            <div className="space-y-3">
              {analytics.low_stock_products.map((product) => (
                <div key={product.id} className="flex justify-between items-center bg-red-50 p-3 rounded">
                  <div>
                    <p className="font-medium text-red-800">{product.name}</p>
                    <p className="text-sm text-red-600">Out of stock</p>
                  </div>
                  <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-sm">
                    Action needed
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`${theme.textSecondary}`}>All products are in stock</p>
          )}
        </div>
      </div>

      {/* Tax Analytics */}
      {analytics.tax_analytics && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className="mr-2">💰</span> Tax Analytics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border-l-4 border-indigo-500 pl-4">
              <p className={`text-sm ${theme.textSecondary}`}>Total Tax Collected</p>
              <p className="text-2xl font-bold text-indigo-600">${analytics.tax_analytics.total_tax.toFixed(2)}</p>
            </div>
            <div className="border-l-4 border-indigo-400 pl-4">
              <p className={`text-sm ${theme.textSecondary}`}>This Week</p>
              <p className="text-2xl font-bold text-indigo-500">${analytics.tax_analytics.week_tax.toFixed(2)}</p>
            </div>
            <div className="border-l-4 border-indigo-300 pl-4">
              <p className={`text-sm ${theme.textSecondary}`}>Today</p>
              <p className="text-2xl font-bold text-indigo-400">${analytics.tax_analytics.today_tax.toFixed(2)}</p>
            </div>
            <div className="border-l-4 border-indigo-200 pl-4">
              <p className={`text-sm ${theme.textSecondary}`}>Avg per Order</p>
              <p className="text-2xl font-bold text-indigo-300">${analytics.tax_analytics.avg_tax_per_order.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Analytics */}
      {analytics.shipping_analytics && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className="mr-2">📦</span> Shipping Analytics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Revenue Metrics</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-cyan-50 rounded">
                  <span className={`${theme.textPrimary}`}>Total Shipping Revenue</span>
                  <span className="font-bold text-cyan-700">${analytics.shipping_analytics.total_shipping_revenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-cyan-50 rounded">
                  <span className={`${theme.textPrimary}`}>This Week</span>
                  <span className="font-bold text-cyan-600">${analytics.shipping_analytics.week_shipping_revenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-cyan-50 rounded">
                  <span className={`${theme.textPrimary}`}>Avg per Order</span>
                  <span className="font-bold text-cyan-500">${analytics.shipping_analytics.avg_shipping_cost.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Delivery Methods</h4>
              <div className="space-y-3">
                {Object.entries(analytics.shipping_analytics.shipping_methods).map(([method, count]) => (
                  <div key={method} className={`flex justify-between items-center p-3 ${theme.bgSecondary} rounded`}>
                    <span className={`capitalize font-medium ${theme.textPrimary}`}>{method}</span>
                    <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full font-semibold">{count} orders</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Analytics */}
      {analytics.payment_analytics && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className="mr-2">💳</span> Payment Analytics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Payment Methods</h4>
              <div className="space-y-3">
                {Object.entries(analytics.payment_analytics.payment_methods).map(([method, count]) => (
                  <div key={method} className="flex justify-between items-center p-3 bg-emerald-50 rounded">
                    <span className={`capitalize font-medium ${theme.textPrimary}`}>{method}</span>
                    <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Revenue by Method</h4>
              <div className="space-y-3">
                {Object.entries(analytics.payment_analytics.payment_method_revenue).map(([method, revenue]) => (
                  <div key={method} className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className={`capitalize font-medium ${theme.textPrimary}`}>{method}</span>
                    <span className="font-bold text-green-700">${revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Payment Status</h4>
              <div className="space-y-3">
                {Object.entries(analytics.payment_analytics.payment_status).map(([status, count]) => (
                  <div key={status} className={`flex justify-between items-center p-3 ${theme.bgSecondary} rounded`}>
                    <span className={`capitalize font-medium ${theme.textPrimary}`}>{status}</span>
                    <span className={`px-3 py-1 rounded-full font-semibold ${
                      status === 'paid' ? 'bg-green-100 text-green-800' :
                      status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profit Metrics */}
      {analytics.profit_metrics && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className="mr-2">📊</span> Profit & Revenue Breakdown
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <h4 className={`font-semibold ${theme.textPrimary}`}>Revenue Components</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                  <span className={`${theme.textPrimary}`}>Product Revenue</span>
                  <span className="font-bold text-blue-700">${analytics.profit_metrics.net_product_revenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-indigo-50 rounded">
                  <span className={`${theme.textPrimary}`}>Tax Collected</span>
                  <span className="font-bold text-indigo-700">${analytics.profit_metrics.total_tax_collected.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-cyan-50 rounded">
                  <span className={`${theme.textPrimary}`}>Shipping Collected</span>
                  <span className="font-bold text-cyan-700">${analytics.profit_metrics.total_shipping_collected.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded border-2 border-green-200">
                  <span className={`font-semibold ${theme.textPrimary}`}>Gross Revenue</span>
                  <span className="font-bold text-xl text-green-700">${analytics.profit_metrics.gross_revenue.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className={`font-semibold ${theme.textPrimary}`}>Average Values</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                  <span className={`${theme.textPrimary}`}>Avg Order Value (All Time)</span>
                  <span className="font-bold text-purple-700">${analytics.profit_metrics.avg_order_value.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                  <span className={`${theme.textPrimary}`}>Avg Order Value (Week)</span>
                  <span className="font-bold text-purple-600">${analytics.profit_metrics.week_avg_order_value.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded">
                  <span className={`${theme.textPrimary}`}>Revenue per Customer</span>
                  <span className="font-bold text-pink-700">${analytics.profit_metrics.revenue_per_customer.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className={`font-semibold ${theme.textPrimary}`}>Key Insights</h4>
              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <p className={`text-sm ${theme.textSecondary} mb-1`}>Product Revenue %</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {((analytics.profit_metrics.net_product_revenue / analytics.profit_metrics.gross_revenue) * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                  <p className={`text-sm ${theme.textSecondary} mb-1`}>Tax %</p>
                  <p className="text-2xl font-bold text-indigo-700">
                    {((analytics.profit_metrics.total_tax_collected / analytics.profit_metrics.gross_revenue) * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg border border-cyan-200">
                  <p className={`text-sm ${theme.textSecondary} mb-1`}>Shipping %</p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {((analytics.profit_metrics.total_shipping_collected / analytics.profit_metrics.gross_revenue) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Visitor Tracking Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full mx-4 p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              ⚠️ Reset Visitor Tracking?
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              Are you sure you want to reset all visitor tracking data? This will permanently delete:
            </p>
            <ul className={`mb-6 ml-6 list-disc ${theme.textSecondary} space-y-1`}>
              <li>All visitor records</li>
              <li>Historical visitor counts</li>
              <li>Unique visitor data</li>
              <li>Conversion rate history</li>
            </ul>
            <p className={`mb-6 text-sm font-semibold text-red-600 dark:text-red-400`}>
              This action cannot be undone!
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                disabled={resetting}
                className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary} disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={confirmResetVisitors}
                disabled={resetting}
                className="px-6 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetting ? 'Resetting...' : 'Yes, Reset Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Orders Management with Bulk Actions
export const EnhancedOrdersManagement = () => {
  const theme = useThemeClasses();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // New state for view/edit modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Calculate cost to make order and profit margin
  const calculateOrderCostAndProfit = (order) => {
    let totalCost = 0;
    let itemBreakdown = [];

    order.items?.forEach((item) => {
      let itemCost = 0;
      let addonCosts = [];
      
      // Get base product cost from cost_breakdown
      if (item.cost_breakdown) {
        const materialCost = item.cost_breakdown.total_material_cost || 0;
        const laborCost = item.cost_breakdown.labor_cost || 0;
        const overheadCost = item.cost_breakdown.overhead_cost || 0;
        itemCost = materialCost + laborCost + overheadCost;
      }
      
      // Add addon costs from selected_addons (enriched data)
      if (item.selected_addons && Array.isArray(item.selected_addons)) {
        item.selected_addons.forEach(addon => {
          if (addon.cost_breakdown) {
            const addonMaterialCost = addon.cost_breakdown.total_material_cost || 0;
            const addonLaborCost = addon.cost_breakdown.labor_cost || 0;
            const addonOverheadCost = addon.cost_breakdown.overhead_cost || 0;
            const addonTotalCost = addonMaterialCost + addonLaborCost + addonOverheadCost;
            itemCost += addonTotalCost;
            
            // Track addon costs for detailed breakdown
            if (addonTotalCost > 0) {
              addonCosts.push({
                name: addon.name || 'Add-on',
                cost: addonTotalCost
              });
            }
          }
        });
      }
      
      const itemTotalCost = itemCost * (item.quantity || 1);
      totalCost += itemTotalCost;
      
      itemBreakdown.push({
        name: item.product_name || item.name || 'Unknown',
        costPerUnit: itemCost,
        quantity: item.quantity || 1,
        totalCost: itemTotalCost,
        addonCosts: addonCosts // Include addon costs for display
      });
    });

    // Use subtotal (not total) for profit calculation - shipping and tax are not business profit
    const orderSubtotal = order.subtotal || 0;
    const profitAmount = orderSubtotal - totalCost;
    const profitMargin = orderSubtotal > 0 ? (profitAmount / orderSubtotal * 100) : 0;

    return {
      totalCost,
      profitAmount,
      profitMargin,
      itemBreakdown
    };
  };

  const searchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('q', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_method', paymentFilter);
      
      const response = await axios.get(`${API}/admin/orders/search?${params}`);
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to search orders:', error);
      toast.error('Failed to search orders');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = (action, value) => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders first');
      return;
    }

    // Store the pending action and show custom confirmation dialog
    setPendingBulkAction({ action, value });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    if (!pendingBulkAction) return;
    
    const { action, value } = pendingBulkAction;
    setShowConfirmDialog(false);

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(`${API}/admin/orders/bulk-update`, {
        order_ids: selectedOrders,
        action: action,
        value: value
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success(response.data.message || `Successfully ${action === 'delete' ? 'deleted' : 'updated'} ${selectedOrders.length} orders`);
      setSelectedOrders([]);
      setShowBulkActions(false);
      setPendingBulkAction(null);
      loadOrders();
    } catch (error) {
      console.error('Bulk action failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk action failed. Please try again.');
    }
  };

  const cancelBulkAction = () => {
    setShowConfirmDialog(false);
    setPendingBulkAction(null);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  const selectAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  const updateOrderStatus = async (orderId, newStatus, orderUpdates = {}) => {
    try {
      const updateData = {
        order_status: newStatus,
        ...orderUpdates
      };
      
      const response = await axios.put(`${API}/admin/orders/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      toast.success(`Order updated successfully`);
      loadOrders(); // Reload orders to show updated data
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleViewOrder = async (order) => {
    // Enrich order items with cost_breakdown from products
    try {
      const enrichedItems = await Promise.all(
        (order.items || []).map(async (item) => {
          try {
            // Fetch product details including cost_breakdown
            const productResponse = await axios.get(`${API}/products/${item.product_id}`);
            const product = productResponse.data;
            
            // Enrich item with cost_breakdown and base price from product
            const enrichedItem = {
              ...item,
              cost_breakdown: product.cost_breakdown || null,
              product_base_price: product.price || 0  // Store the actual product base price
            };
            
            // Transform selected_addons from IDs to full addon objects with names and prices
            if (enrichedItem.selected_addons && Array.isArray(enrichedItem.selected_addons) && product.addons) {
              enrichedItem.selected_addons = enrichedItem.selected_addons.map(addonIdOrObj => {
                // If addon is already an object with name and price, keep it as-is
                if (typeof addonIdOrObj === 'object' && addonIdOrObj.name && addonIdOrObj.price !== undefined) {
                  return addonIdOrObj;
                }
                
                // Otherwise, treat it as an ID and find the addon in the product's addons array
                const addon = product.addons.find(a => a.id === addonIdOrObj);
                if (addon) {
                  return {
                    id: addon.id,
                    name: addon.name || addon.custom_name || 'Add-on',
                    price: addon.price || 0,
                    linked_product_id: addon.linked_product_id
                  };
                }
                // If addon not found, return a placeholder object
                return {
                  id: addonIdOrObj,
                  name: 'Unknown Add-on',
                  price: 0
                };
              });
              
              // Calculate addon_total from enriched addons
              enrichedItem.addon_total = enrichedItem.selected_addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
            }
            
            // Enrich selected_options with prices from product's base_options
            if (enrichedItem.selected_options && product.base_options) {
              let optionTotal = 0;
              const enrichedOptions = {};
              
              // Handle both object and array formats
              let selectedOpts = {};
              
              if (Array.isArray(enrichedItem.selected_options)) {
                // Convert array format to object format
                // Array format: [{name: "Size", value: "75 MM", price: 10}]
                enrichedItem.selected_options.forEach(opt => {
                  if (opt.name && opt.value !== undefined) {
                    selectedOpts[opt.name] = {
                      value: opt.value,
                      price: opt.price || 0
                    };
                    optionTotal += opt.price || 0;
                  }
                });
                enrichedItem.selected_options = selectedOpts;
                enrichedItem.option_total = optionTotal;
              } else if (typeof enrichedItem.selected_options === 'object') {
                // Object format: {"Size": "75 MM"} or {"Size": {value: "75 MM", price: 10}}
                selectedOpts = enrichedItem.selected_options;
              
                Object.entries(selectedOpts).forEach(([optionKey, optionValue]) => {
                const selectedVal = typeof optionValue === 'object' ? optionValue.value : optionValue;
                
                // Find this option in product's base_options
                if (product.base_options[optionKey] && Array.isArray(product.base_options[optionKey])) {
                  const optionDef = product.base_options[optionKey].find(opt => opt.name === selectedVal);
                  if (optionDef) {
                    enrichedOptions[optionKey] = {
                      value: selectedVal,
                      price: optionDef.price || 0
                    };
                    optionTotal += optionDef.price || 0;
                  } else {
                    enrichedOptions[optionKey] = {
                      value: selectedVal,
                      price: 0
                    };
                  }
                } else {
                  // Keep original if can't find in product
                  enrichedOptions[optionKey] = typeof optionValue === 'object' ? optionValue : { value: optionValue, price: 0 };
                }
              });
              
              enrichedItem.selected_options = enrichedOptions;
              enrichedItem.option_total = optionTotal;
            }
            }
            
            // Enrich addons with cost_breakdown if they exist (for linked product addons)
            if (enrichedItem.selected_addons && Array.isArray(enrichedItem.selected_addons)) {
              enrichedItem.selected_addons = await Promise.all(
                enrichedItem.selected_addons.map(async (addon) => {
                  if (addon.linked_product_id) {
                    try {
                      const addonProductResponse = await axios.get(`${API}/products/${addon.linked_product_id}`);
                      return {
                        ...addon,
                        cost_breakdown: addonProductResponse.data.cost_breakdown || null
                      };
                    } catch (err) {
                      console.error('Failed to fetch addon product:', err);
                      return addon;
                    }
                  }
                  return addon;
                })
              );
            }
            
            return enrichedItem;
          } catch (error) {
            console.error('Failed to fetch product for cost calculation:', error);
            return item;
          }
        })
      );
      
      setSelectedOrder({
        ...order,
        items: enrichedItems
      });
    } catch (error) {
      console.error('Failed to enrich order:', error);
      setSelectedOrder(order);
    }
    
    setShowViewModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditFormData({
      order_status: order.order_status || 'processing',
      payment_status: order.payment_status || 'pending',
      payment_method: order.payment_method || 'paypal',
      shipping_method: order.shipping_method || 'standard',
      include_tax: order.include_tax !== false, // Default to true unless explicitly false
      tax_amount: order.tax_amount || 0,
      subtotal: order.subtotal || order.total || 0,
      total: order.total || 0,
      notes: order.notes || '',
      tracking_number: order.tracking_number || ''
    });
    setShowEditModal(true);
  };

  const handleSaveOrderEdit = async () => {
    if (!selectedOrder) return;
    
    try {
      // Recalculate totals based on tax setting
      const subtotal = parseFloat(editFormData.subtotal) || 0;
      const taxAmount = editFormData.include_tax ? subtotal * 0.08 : 0; // 8% tax rate
      const total = subtotal + taxAmount;
      
      const updateData = {
        ...editFormData,
        tax_amount: taxAmount,
        total: total
      };
      
      await updateOrderStatus(selectedOrder.id, editFormData.order_status, updateData);
      setShowEditModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error saving order edit:', error);
      toast.error('Failed to save order changes');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Orders Management</h2>
        <div className="flex space-x-2">
          {selectedOrders.length > 0 && (
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700"
            >
              Bulk Actions ({selectedOrders.length})
            </button>
          )}
          <button
            onClick={loadOrders}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Search & Filter</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search orders, customers, order numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">All Statuses</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">All Payment Methods</option>
              <option value="paypal">PayPal</option>
              <option value="etransfer">E-Transfer</option>
            </select>
          </div>
          <div>
            <button
              onClick={searchOrders}
              className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {showBulkActions && selectedOrders.length > 0 && (
        <div className={`${theme.card} rounded-lg shadow p-6 border-l-4 border-orange-500`}>
          <h3 className="text-lg font-semibold mb-4">Bulk Actions ({selectedOrders.length} orders selected)</h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleBulkAction('status', 'processing')}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
            >
              Mark as Processing
            </button>
            <button
              onClick={() => handleBulkAction('status', 'shipped')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Mark as Shipped
            </button>
            <button
              onClick={() => handleBulkAction('status', 'delivered')}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Mark as Delivered
            </button>
            <button
              onClick={() => handleBulkAction('payment_status', 'paid')}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
            >
              Mark as Paid
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Orders ({orders.length})</h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedOrders.length === orders.length && orders.length > 0}
                onChange={selectAllOrders}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label className="text-sm font-medium">Select All</label>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left`}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.length === orders.length && orders.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Order #</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Customer</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Total</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Payment</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Date</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {orders.map((order) => (
                  <tr key={order.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700 ${selectedOrders.includes(order.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm font-bold text-blue-600">
                        {order.order_number || `#${order.id.slice(-8)}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>
                          {order.customer_info?.name || `${order.customer_info?.first_name || ''} ${order.customer_info?.last_name || ''}`.trim() || 'N/A'}
                        </div>
                        <div className={`text-sm ${theme.textSecondary}`}>{order.customer_info?.email || order.customer_email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold">${order.total.toFixed(2)}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>{order.items.length} item(s)</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.order_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.payment_method} - {order.payment_status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEditOrder(order)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                  📋 Order Details - {selectedOrder.order_number || `#${selectedOrder.id?.slice(-8)}`}
                </h2>
                <button 
                  onClick={() => setShowViewModal(false)}
                  className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary}`}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Information */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>👤 Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Name</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.customer_info?.name || `${selectedOrder.customer_info?.first_name || ''} ${selectedOrder.customer_info?.last_name || ''}`.trim() || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Email</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.customer_info?.email || selectedOrder.customer_email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Phone</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.customer_info?.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Order Date</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Order Number</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.order_number || selectedOrder.id?.slice(-8) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Delivery Method</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.delivery_method === 'shipping' ? 'Shipping' : selectedOrder.delivery_method === 'pickup' ? 'Local Pickup' : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>🛍️ Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items?.map((item, index) => {
                    // Calculate totals dynamically from selected_options and selected_addons
                    const basePrice = item.product_base_price || item.price || 0;
                    
                    // Calculate option total
                    let optionTotal = item.option_total || 0;
                    if (!item.option_total && item.selected_options) {
                      optionTotal = (Array.isArray(item.selected_options) ? item.selected_options : Object.values(item.selected_options))
                        .reduce((sum, opt) => sum + (typeof opt === 'object' ? (opt.price || 0) : 0), 0);
                    }
                    
                    // Calculate addon total
                    let addonTotal = item.addon_total || 0;
                    if (!item.addon_total && item.selected_addons) {
                      addonTotal = item.selected_addons
                        .reduce((sum, addon) => sum + (typeof addon === 'object' ? (addon.price || 0) : 0), 0);
                    }
                    
                    // Calculate personalization total
                    let personalizationTotal = item.personalization_price || 0;
                    if (!item.personalization_price && item.custom_text_inputs) {
                      personalizationTotal = (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs : Object.values(item.custom_text_inputs))
                        .reduce((sum, input) => sum + (typeof input === 'object' ? (input.price || 0) : 0), 0);
                    }
                    
                    const pricePerItem = basePrice + optionTotal + addonTotal + personalizationTotal;
                    
                    return (
                    <div key={index} className={`p-4 rounded border ${theme.border} ${theme.bgPrimary}`}>
                      <div className="mb-3">
                        <p className={`font-medium text-lg ${theme.textPrimary} mb-3`}>
                          {item.product_name || item.name || 'Unknown Product'}
                        </p>
                        
                        {/* Itemized Price List */}
                        <div className={`space-y-2 text-sm`}>
                          {/* Base Product */}
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <span className={theme.textPrimary}>Product (Base Price)</span>
                            </div>
                            <span className={`font-medium ${theme.textPrimary}`}>${basePrice.toFixed(2)}</span>
                          </div>
                          
                          {/* Options with prices */}
                          {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options : Object.entries(item.selected_options)).map((option, i) => {
                            let optionName = '';
                            let optionValue = '';
                            let optionPrice = 0;
                            
                            if (Array.isArray(item.selected_options)) {
                              optionName = option.name || option.key || 'Option';
                              optionValue = option.value;
                              optionPrice = option.price || 0;
                            } else {
                              optionName = option[0];
                              optionValue = typeof option[1] === 'object' ? option[1].value : option[1];
                              optionPrice = typeof option[1] === 'object' ? (option[1].price || 0) : 0;
                            }
                            
                            console.log('Option Debug:', { optionName, optionValue, optionPrice, option, rawData: item.selected_options });
                            
                            // Always show options, even if price is 0, but indicate if there's a price
                            return (
                              <div key={i} className="flex justify-between items-center">
                                <div className="flex-1">
                                  <span className="text-blue-600">+ {optionName}: {optionValue}</span>
                                </div>
                                <span className="font-medium text-blue-600">+${optionPrice.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          
                          {/* Add-ons with names and prices */}
                          {/* Cache bust: 2025-10-16-15:03 */}
                          {item.selected_addons && item.selected_addons.map((addon, i) => {
                            const addonName = typeof addon === 'object' ? (addon.name || addon.custom_name || 'Add-on') : 'Add-on';
                            const addonPrice = typeof addon === 'object' ? (addon.price || 0) : 0;
                            
                            console.log('Addon Debug:', { addonName, addonPrice, addon, fullAddonObject: JSON.stringify(addon) });
                            
                            return (
                              <div key={i} className="flex justify-between items-center">
                                <div className="flex-1">
                                  <span className="text-green-600">+ Add-on: {addonName}</span>
                                </div>
                                <span className="font-medium text-green-600">+${addonPrice.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          
                          {/* Custom text/personalization (if has price) */}
                          {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs : Object.entries(item.custom_text_inputs)).map((input, i) => {
                            let inputPrice = 0;
                            let inputName = '';
                            
                            if (Array.isArray(item.custom_text_inputs)) {
                              inputPrice = input.price || 0;
                              inputName = input.name || 'Personalization';
                            } else {
                              inputPrice = typeof input[1] === 'object' ? (input[1].price || 0) : 0;
                              inputName = input[0];
                            }
                            
                            if (inputPrice > 0) {
                              return (
                                <div key={i} className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <span className="text-purple-600">+ {inputName}</span>
                                  </div>
                                  <span className="font-medium text-purple-600">+${inputPrice.toFixed(2)}</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                          
                          {/* Subtotal per item */}
                          <div className={`flex justify-between items-center pt-2 border-t ${theme.border}`}>
                            <span className={`font-medium ${theme.textPrimary}`}>Price per Item:</span>
                            <span className={`font-bold ${theme.textPrimary}`}>${pricePerItem.toFixed(2)}</span>
                          </div>
                          
                          {/* Quantity */}
                          <div className="flex justify-between items-center">
                            <span className={theme.textSecondary}>Quantity:</span>
                            <span className={`font-medium ${theme.textSecondary}`}>× {item.quantity || 1}</span>
                          </div>
                          
                          {/* Item Total */}
                          <div className={`flex justify-between items-center pt-2 border-t ${theme.border}`}>
                            <span className={`font-bold text-lg ${theme.textPrimary}`}>Item Total:</span>
                            <span className="font-bold text-xl text-blue-600">
                              ${(pricePerItem * (item.quantity || 1)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Selected Options */}
                      {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options.length > 0 : Object.keys(item.selected_options).length > 0) && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Options:</p>
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(item.selected_options) ? (
                              // Handle array format: [{name: "Size", value: "50MM", price: 0}]
                              item.selected_options.map((option, i) => (
                                <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                                  {option.name || option.key || 'Option'}: {option.value}
                                </span>
                              ))
                            ) : (
                              // Handle object format: {Size: "50MM"}
                              Object.entries(item.selected_options).map(([key, value], i) => (
                                <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                                  {key}: {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Custom Text Inputs */}
                      {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs.length > 0 : Object.keys(item.custom_text_inputs).length > 0) && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Personalization:</p>
                          <div className="space-y-1">
                            {Array.isArray(item.custom_text_inputs) ? (
                              // Handle array format: [{name: "Line 1", value: "John Doe"}]
                              item.custom_text_inputs.map((input, i) => (
                                <p key={i} className={`text-sm ${theme.textSecondary} bg-purple-50 px-3 py-1 rounded`}>
                                  <strong>{input.name || input.key || 'Text'}:</strong> {input.value}
                                </p>
                              ))
                            ) : (
                              // Handle object format: {"Line 1": "John Doe"}
                              Object.entries(item.custom_text_inputs).map(([key, value], i) => (
                                <p key={i} className={`text-sm ${theme.textSecondary} bg-purple-50 px-3 py-1 rounded`}>
                                  <strong>{key}:</strong> {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                </p>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Selected Addons */}
                      {item.selected_addons && item.selected_addons.length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Add-ons:</p>
                          <div className="space-y-2">
                            {item.selected_addons.map((addonIdOrObj, i) => {
                              // Handle both addon ID (string) and addon object formats
                              let addonName = 'Unknown Add-on';
                              let addonPrice = 0;
                              
                              if (typeof addonIdOrObj === 'string') {
                                // It's an addon ID - need to fetch from product (should be done by enrichment)
                                addonName = `Add-on ${i + 1}`;
                              } else if (addonIdOrObj && typeof addonIdOrObj === 'object') {
                                // It's an addon object
                                addonName = addonIdOrObj.name || addonIdOrObj.custom_name || 'Add-on';
                                addonPrice = addonIdOrObj.price || 0;
                              }
                              
                              return (
                                <div key={i} className="inline-block bg-green-100 text-green-800 px-3 py-2 rounded text-sm">
                                  <span className="font-medium">{addonName}</span>
                                  {addonPrice > 0 && (
                                    <span className="ml-2 text-green-600">(+${addonPrice.toFixed(2)})</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Legacy Customization Field (from manual orders) */}
                      {item.customization && Object.keys(item.customization).length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>📝 Customization Details:</p>
                          <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded space-y-1">
                            {Object.entries(item.customization).map(([key, value], i) => (
                              <p key={i} className={`text-sm ${theme.textSecondary}`}>
                                <strong className="text-purple-700">{key}:</strong> {value}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Customization Notes */}
                      {item.customization_notes && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>💬 Additional Notes:</p>
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                            <p className={`text-sm ${theme.textSecondary} whitespace-pre-wrap`}>{item.customization_notes}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Uploaded Files */}
                      {item.uploaded_files && item.uploaded_files.length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-2`}>📎 Customer Uploaded Files:</p>
                          <div className="grid grid-cols-1 gap-2">
                            {item.uploaded_files.map((file, fileIndex) => {
                              const isImage = file.content_type?.startsWith('image/');
                              const fileUrl = file.file_url || `/api/files/${file.filename}`;
                              const displayName = file.original_filename || file.filename || `File ${fileIndex + 1}`;
                              
                              return (
                                <div key={fileIndex} className="flex items-center justify-between p-2 rounded bg-blue-50 border border-blue-200">
                                  <div className="flex items-center gap-3 flex-1">
                                    {isImage && file.thumbnail_url ? (
                                      <img 
                                        src={`${process.env.REACT_APP_BACKEND_URL}${file.thumbnail_url}`}
                                        alt={displayName}
                                        className="w-12 h-12 object-cover rounded border"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-12 h-12 flex items-center justify-center rounded bg-white border">
                                        <span className="text-2xl">{isImage ? '🖼️' : '📄'}</span>
                                      </div>
                                    )}
                                    
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>
                                        {displayName}
                                      </p>
                                      {file.file_size && (
                                        <p className="text-xs text-gray-600">
                                          {(file.file_size / 1024).toFixed(1)} KB
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2">
                                    {isImage && (
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 transition"
                                        title="View image"
                                      >
                                        👁️ View
                                      </a>
                                    )}
                                    <a
                                      href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                      download={displayName}
                                      className="px-3 py-1 text-xs rounded bg-green-500 text-white hover:bg-green-600 transition"
                                      title="Download file"
                                    >
                                      ⬇️ Download
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              </div>

              {/* Order Status & Payment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>📦 Order Status</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedOrder.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                        selectedOrder.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        selectedOrder.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedOrder.order_status || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Shipping:</span>
                      <span className={theme.textPrimary}>{selectedOrder.shipping_method || 'N/A'}</span>
                    </div>
                    {selectedOrder.tracking_number && (
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Tracking:</span>
                        <span className={`font-mono text-sm ${theme.textPrimary}`}>{selectedOrder.tracking_number}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💳 Payment & Total</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Payment Method:</span>
                      <span className={theme.textPrimary}>{selectedOrder.payment_method || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Payment Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        selectedOrder.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedOrder.payment_status || 'Unknown'}
                      </span>
                    </div>
                    <div className={`mt-3 pt-3 border-t ${theme.border}`}></div>
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Subtotal:</span>
                      <span className={theme.textPrimary}>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {(selectedOrder.tax_amount > 0 || selectedOrder.tax > 0) && (
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Tax:</span>
                        <span className={theme.textPrimary}>${((selectedOrder.tax_amount || selectedOrder.tax) || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.shipping_cost > 0 && (
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Shipping:</span>
                        <span className={theme.textPrimary}>${selectedOrder.shipping_cost.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.discount_amount > 0 && (
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Discount:</span>
                        <span className="text-red-600">-${selectedOrder.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                      <span className={`font-bold ${theme.textPrimary}`}>Total:</span>
                      <span className="text-xl font-bold text-green-600">${(selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                    {selectedOrder.deposit_amount > 0 && (
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Deposit Paid:</span>
                        <span className="text-green-600">${selectedOrder.deposit_amount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.balance_owed > 0 && (
                      <div className="flex justify-between">
                        <span className={`font-bold ${theme.textSecondary}`}>Balance Owed:</span>
                        <span className="text-xl font-bold text-orange-600">${selectedOrder.balance_owed.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment History */}
              {selectedOrder.payment_history && selectedOrder.payment_history.length > 0 && (
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💰 Payment History</h3>
                  <div className="space-y-2">
                    {selectedOrder.payment_history.map((payment, index) => (
                      <div key={index} className={`p-3 rounded border ${theme.border} ${theme.bgPrimary}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                payment.type === 'deposit' ? 'bg-blue-100 text-blue-800' :
                                payment.type === 'full_payment' ? 'bg-green-100 text-green-800' :
                                payment.type === 'partial_payment' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {payment.type === 'deposit' ? '📥 Deposit' :
                                 payment.type === 'full_payment' ? '✅ Full Payment' :
                                 payment.type === 'partial_payment' ? '💳 Partial Payment' :
                                 payment.type}
                              </span>
                              <span className={`text-sm ${theme.textSecondary}`}>
                                {new Date(payment.date).toLocaleString()}
                              </span>
                            </div>
                            <p className={`text-sm ${theme.textSecondary}`}>
                              Method: <span className={`font-medium ${theme.textPrimary}`}>{payment.method || 'N/A'}</span>
                            </p>
                            {payment.note && (
                              <p className={`text-xs ${theme.textSecondary} mt-1 italic`}>{payment.note}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              ${(payment.amount || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className={`pt-3 border-t ${theme.border} flex justify-between items-center`}>
                      <span className={`font-semibold ${theme.textPrimary}`}>Total Paid:</span>
                      <span className="text-xl font-bold text-green-600">
                        ${selectedOrder.payment_history.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Creation Info */}
              {selectedOrder.created_by_admin && (
                <div className={`p-4 rounded-lg border ${theme.border} bg-blue-50 dark:bg-blue-900/20`}>
                  <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>👤 Order Created By</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    This order was manually created by admin: <span className={`font-medium ${theme.textPrimary}`}>{selectedOrder.created_by_admin}</span>
                  </p>
                </div>
              )}

              {/* Cost & Profit Analysis */}
              {(() => {
                const costData = calculateOrderCostAndProfit(selectedOrder);
                const hasCosting = costData.totalCost > 0;
                
                return hasCosting && (
                  <div className={`p-4 rounded-lg border ${theme.border} bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20`}>
                    <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💰 Cost & Profit Analysis</h3>
                    
                    {/* Item-by-item breakdown */}
                    {costData.itemBreakdown.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <p className={`text-sm font-medium ${theme.textSecondary} mb-2`}>Cost Breakdown by Item:</p>
                        {costData.itemBreakdown.map((item, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className={`flex justify-between text-sm p-2 rounded ${theme.bgPrimary} border ${theme.border}`}>
                              <span className={theme.textPrimary}>
                                {item.name} (x{item.quantity})
                                <span className={`ml-2 text-xs ${theme.textSecondary}`}>
                                  ${item.costPerUnit.toFixed(2)}/unit
                                </span>
                              </span>
                              <span className={theme.textPrimary}>${item.totalCost.toFixed(2)}</span>
                            </div>
                            {/* Show addon costs if any */}
                            {item.addonCosts && item.addonCosts.length > 0 && (
                              <div className="ml-4 space-y-1">
                                {item.addonCosts.map((addon, addonIdx) => (
                                  <div key={addonIdx} className={`flex justify-between text-xs p-1.5 rounded ${theme.bgSecondary}`}>
                                    <span className={`${theme.textSecondary} italic`}>
                                      ↳ {addon.name} (add-on cost)
                                    </span>
                                    <span className={`${theme.textSecondary}`}>${addon.cost.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="space-y-2">
                      <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                        <span className={`font-semibold ${theme.textPrimary}`}>Total Cost to Make:</span>
                        <span className={`font-bold ${theme.textPrimary}`}>${costData.totalCost.toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className={`font-semibold ${theme.textPrimary}`}>Order Subtotal (Revenue):</span>
                        <span className={`font-bold ${theme.textPrimary}`}>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                      </div>
                      
                      <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                        <span className={`font-bold text-lg ${theme.textPrimary}`}>Profit Amount:</span>
                        <span className={`font-bold text-xl ${costData.profitAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {costData.profitAmount >= 0 ? '+' : ''}${costData.profitAmount.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className={`font-bold text-lg ${theme.textPrimary}`}>Profit Margin:</span>
                        <span className={`font-bold text-xl ${costData.profitMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {costData.profitMargin.toFixed(2)}%
                        </span>
                      </div>
                      
                      {costData.profitMargin < 0 && (
                        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded text-sm">
                          <p className="text-red-800 dark:text-red-200 font-medium">
                            ⚠️ Warning: This order is losing ${Math.abs(costData.profitAmount).toFixed(2)}
                          </p>
                        </div>
                      )}
                      
                      {costData.profitMargin >= 0 && costData.profitMargin < 10 && (
                        <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded text-sm">
                          <p className="text-amber-800 dark:text-amber-200">
                            💡 Low profit margin for this order
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>📍 Shipping Address</h3>
                  <p className={theme.textPrimary}>
                    {selectedOrder.shipping_address.street}<br/>
                    {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postal_code}<br/>
                    {selectedOrder.shipping_address.country}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>📝 Order Notes</h3>
                  <p className={theme.textPrimary}>{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                  ✏️ Edit Order - {selectedOrder.order_number || `#${selectedOrder.id?.slice(-8)}`}
                </h2>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary}`}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Status Section */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📦 Order Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Order Status</label>
                    <select
                      value={editFormData.order_status}
                      onChange={(e) => setEditFormData({...editFormData, order_status: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Payment Status</label>
                    <select
                      value={editFormData.payment_status}
                      onChange={(e) => setEditFormData({...editFormData, payment_status: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Payment Method</label>
                    <select
                      value={editFormData.payment_method}
                      onChange={(e) => setEditFormData({...editFormData, payment_method: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="paypal">PayPal</option>
                      <option value="etransfer">E-Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="credit_card">Credit Card</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Shipping Method</label>
                    <select
                      value={editFormData.shipping_method}
                      onChange={(e) => setEditFormData({...editFormData, shipping_method: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="standard">Standard</option>
                      <option value="express">Express</option>
                      <option value="overnight">Overnight</option>
                      <option value="pickup">Local Pickup</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Tracking Number</label>
                  <input
                    type="text"
                    value={editFormData.tracking_number}
                    onChange={(e) => setEditFormData({...editFormData, tracking_number: e.target.value})}
                    placeholder="Enter tracking number (optional)"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
              </div>

              {/* Pricing & Tax Section */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>💰 Pricing & Tax</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Subtotal</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.subtotal}
                      onChange={(e) => setEditFormData({...editFormData, subtotal: parseFloat(e.target.value) || 0})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      <input
                        type="checkbox"
                        checked={editFormData.include_tax}
                        onChange={(e) => setEditFormData({...editFormData, include_tax: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      Include Tax (8%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.include_tax ? (editFormData.subtotal * 0.08).toFixed(2) : 0}
                      disabled
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgSecondary} ${theme.textSecondary}`}
                    />
                  </div>
                </div>
                
                <div className={`mt-4 p-3 rounded ${theme.isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${theme.isDark ? 'border-blue-700' : 'border-blue-200'}`}>
                  <div className="flex justify-between text-lg font-bold">
                    <span className={theme.textPrimary}>New Total:</span>
                    <span className="text-green-600">
                      ${(editFormData.subtotal + (editFormData.include_tax ? editFormData.subtotal * 0.08 : 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Notes */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📝 Order Notes</h3>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  rows="3"
                  placeholder="Add notes about this order..."
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrderEdit}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      {showConfirmDialog && pendingBulkAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full mx-4 p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              Confirm Action
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              {pendingBulkAction.action === 'delete'
                ? `Are you sure you want to delete ${selectedOrders.length} order${selectedOrders.length > 1 ? 's' : ''}? This action cannot be undone.`
                : `Are you sure you want to update ${selectedOrders.length} order${selectedOrders.length > 1 ? 's' : ''}?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelBulkAction}
                className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkAction}
                className={`px-6 py-2 rounded-lg text-white ${
                  pendingBulkAction.action === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {pendingBulkAction.action === 'delete' ? 'Delete' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Created Orders Management - Orders created by admin via Create Order tab
export const CreatedOrdersManagement = () => {
  const theme = useThemeClasses();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Bulk action states
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);

  useEffect(() => {
    loadCreatedOrders();
  }, []);

  useEffect(() => {
    setShowBulkActions(selectedOrders.length > 0);
  }, [selectedOrders]);

  const loadCreatedOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/created-orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Failed to load created orders:', error);
      toast.error('Failed to load created orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate cost to make order and profit margin
  const calculateOrderCostAndProfit = (order) => {
    let totalCost = 0;
    let itemBreakdown = [];

    order.items?.forEach((item) => {
      let itemCost = 0;
      let addonCosts = [];
      
      // Get base product cost from cost_breakdown
      if (item.cost_breakdown) {
        const materialCost = item.cost_breakdown.total_material_cost || 0;
        const laborCost = item.cost_breakdown.labor_cost || 0;
        const overheadCost = item.cost_breakdown.overhead_cost || 0;
        itemCost = materialCost + laborCost + overheadCost;
      }
      
      // Add addon costs from selected_addons (enriched data)
      if (item.selected_addons && Array.isArray(item.selected_addons)) {
        item.selected_addons.forEach(addon => {
          if (addon.cost_breakdown) {
            const addonMaterialCost = addon.cost_breakdown.total_material_cost || 0;
            const addonLaborCost = addon.cost_breakdown.labor_cost || 0;
            const addonOverheadCost = addon.cost_breakdown.overhead_cost || 0;
            const addonTotalCost = addonMaterialCost + addonLaborCost + addonOverheadCost;
            itemCost += addonTotalCost;
            
            // Track addon costs for detailed breakdown
            if (addonTotalCost > 0) {
              addonCosts.push({
                name: addon.name || 'Add-on',
                cost: addonTotalCost
              });
            }
          }
        });
      }
      
      const itemTotalCost = itemCost * (item.quantity || 1);
      totalCost += itemTotalCost;
      
      itemBreakdown.push({
        name: item.product_name || item.name || 'Unknown',
        costPerUnit: itemCost,
        quantity: item.quantity || 1,
        totalCost: itemTotalCost,
        addonCosts: addonCosts // Include addon costs for display
      });
    });

    // Use subtotal (not total) for profit calculation - shipping and tax are not business profit
    const orderSubtotal = order.subtotal || 0;
    const profitAmount = orderSubtotal - totalCost;
    const profitMargin = orderSubtotal > 0 ? (profitAmount / orderSubtotal * 100) : 0;

    return {
      totalCost,
      profitAmount,
      profitMargin,
      itemBreakdown
    };
  };

  const handleViewOrder = async (order) => {
    // Enrich order items with cost_breakdown from products
    try {
      const enrichedItems = await Promise.all(
        (order.items || []).map(async (item) => {
          try {
            // Fetch product details including cost_breakdown
            const productResponse = await axios.get(`${API}/products/${item.product_id}`);
            const product = productResponse.data;
            
            // Enrich item with cost_breakdown and base price from product
            const enrichedItem = {
              ...item,
              cost_breakdown: product.cost_breakdown || null,
              product_base_price: product.price || 0  // Store the actual product base price
            };
            
            // Transform selected_addons from IDs to full addon objects with names and prices
            if (enrichedItem.selected_addons && Array.isArray(enrichedItem.selected_addons) && product.addons) {
              enrichedItem.selected_addons = enrichedItem.selected_addons.map(addonIdOrObj => {
                // If addon is already an object with name and price, keep it as-is
                if (typeof addonIdOrObj === 'object' && addonIdOrObj.name && addonIdOrObj.price !== undefined) {
                  return addonIdOrObj;
                }
                
                // Otherwise, treat it as an ID and find the addon in the product's addons array
                const addon = product.addons.find(a => a.id === addonIdOrObj);
                if (addon) {
                  return {
                    id: addon.id,
                    name: addon.name || addon.custom_name || 'Add-on',
                    price: addon.price || 0,
                    linked_product_id: addon.linked_product_id
                  };
                }
                // If addon not found, return a placeholder object
                return {
                  id: addonIdOrObj,
                  name: 'Unknown Add-on',
                  price: 0
                };
              });
              
              // Calculate addon_total from enriched addons
              enrichedItem.addon_total = enrichedItem.selected_addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
            }
            
            // Enrich selected_options with prices from product's base_options
            if (enrichedItem.selected_options && product.base_options) {
              let optionTotal = 0;
              const enrichedOptions = {};
              
              // Handle both object and array formats
              let selectedOpts = {};
              
              if (Array.isArray(enrichedItem.selected_options)) {
                // Convert array format to object format
                // Array format: [{name: "Size", value: "75 MM", price: 10}]
                enrichedItem.selected_options.forEach(opt => {
                  if (opt.name && opt.value !== undefined) {
                    selectedOpts[opt.name] = {
                      value: opt.value,
                      price: opt.price || 0
                    };
                    optionTotal += opt.price || 0;
                  }
                });
                enrichedItem.selected_options = selectedOpts;
                enrichedItem.option_total = optionTotal;
              } else if (typeof enrichedItem.selected_options === 'object') {
                // Object format: {"Size": "75 MM"} or {"Size": {value: "75 MM", price: 10}}
                selectedOpts = enrichedItem.selected_options;
              
                Object.entries(selectedOpts).forEach(([optionKey, optionValue]) => {
                const selectedVal = typeof optionValue === 'object' ? optionValue.value : optionValue;
                
                // Find this option in product's base_options
                if (product.base_options[optionKey] && Array.isArray(product.base_options[optionKey])) {
                  const optionDef = product.base_options[optionKey].find(opt => opt.name === selectedVal);
                  if (optionDef) {
                    enrichedOptions[optionKey] = {
                      value: selectedVal,
                      price: optionDef.price || 0
                    };
                    optionTotal += optionDef.price || 0;
                  } else {
                    enrichedOptions[optionKey] = {
                      value: selectedVal,
                      price: 0
                    };
                  }
                } else {
                  // Keep original if can't find in product
                  enrichedOptions[optionKey] = typeof optionValue === 'object' ? optionValue : { value: optionValue, price: 0 };
                }
              });
              
              enrichedItem.selected_options = enrichedOptions;
              enrichedItem.option_total = optionTotal;
            }
            }
            
            // Enrich addons with cost_breakdown if they exist (for linked product addons)
            if (enrichedItem.selected_addons && Array.isArray(enrichedItem.selected_addons)) {
              enrichedItem.selected_addons = await Promise.all(
                enrichedItem.selected_addons.map(async (addon) => {
                  if (addon.linked_product_id) {
                    try {
                      const addonProductResponse = await axios.get(`${API}/products/${addon.linked_product_id}`);
                      return {
                        ...addon,
                        cost_breakdown: addonProductResponse.data.cost_breakdown || null
                      };
                    } catch (err) {
                      console.error('Failed to fetch addon product:', err);
                      return addon;
                    }
                  }
                  return addon;
                })
              );
            }
            
            return enrichedItem;
          } catch (error) {
            console.error('Failed to fetch product for cost calculation:', error);
            return item;
          }
        })
      );
      
      setSelectedOrder({
        ...order,
        items: enrichedItems
      });
    } catch (error) {
      console.error('Failed to enrich order:', error);
      setSelectedOrder(order);
    }
    
    setShowViewModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditFormData({
      order_status: order.order_status || 'processing',
      payment_status: order.payment_status || 'pending',
      payment_method: order.payment_method || 'paypal',
      shipping_method: order.shipping_method || 'standard',
      include_tax: order.include_tax !== false,
      tax_amount: order.tax_amount || 0,
      subtotal: order.subtotal || order.total || 0,
      total: order.total || 0,
      notes: order.notes || '',
      tracking_number: order.tracking_number || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteOrder = (order) => {
    setSelectedOrder(order);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${selectedOrder.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      toast.success('Order deleted successfully');
      setShowDeleteModal(false);
      setSelectedOrder(null);
      loadCreatedOrders(); // Refresh the list
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateOrderStatus = async (orderId, newStatus, orderUpdates = {}) => {
    try {
      const updateData = {
        order_status: newStatus,
        ...orderUpdates
      };
      
      await axios.put(`${API}/admin/orders/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      toast.success('Order updated successfully');
      loadCreatedOrders(); // Reload orders
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveOrderEdit = async () => {
    if (!selectedOrder) return;
    
    try {
      const subtotal = parseFloat(editFormData.subtotal) || 0;
      const taxAmount = editFormData.include_tax ? subtotal * 0.08 : 0;
      const total = subtotal + taxAmount;
      
      const updateData = {
        ...editFormData,
        tax_amount: taxAmount,
        total: total
      };
      
      await updateOrderStatus(selectedOrder.id, editFormData.order_status, updateData);
      setShowEditModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error saving order edit:', error);
      toast.error('Failed to save order changes');
    }
  };

  const handleResendInvitation = async (order) => {
    try {
      const response = await axios.post(
        `${API}/admin/orders/${order.id}/resend-invitation`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      
      toast.success(`Invitation resent to ${response.data.customer_email}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to resend invitation';
      toast.error(errorMsg);
    }
  };

  // Bulk action handlers
  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(order => order.id));
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleBulkAction = (action, value) => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders first');
      return;
    }

    setPendingBulkAction({ action, value });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    if (!pendingBulkAction) return;
    
    const { action, value } = pendingBulkAction;
    setShowConfirmDialog(false);

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(`${API}/admin/orders/bulk-update`, {
        order_ids: selectedOrders,
        action: action,
        value: value
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success(response.data.message || `Successfully ${action === 'delete' ? 'deleted' : 'updated'} ${selectedOrders.length} orders`);
      setSelectedOrders([]);
      setShowBulkActions(false);
      setPendingBulkAction(null);
      loadCreatedOrders();
    } catch (error) {
      console.error('Bulk action failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk action failed. Please try again.');
    }
  };

  const cancelBulkAction = () => {
    setShowConfirmDialog(false);
    setPendingBulkAction(null);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || order.order_status === statusFilter;
    const matchesPayment = !paymentFilter || order.payment_status === paymentFilter;
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  if (loading) {
    return <div className={`text-center py-8 ${theme.textPrimary}`}>Loading created orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>📋 Created Orders</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>
            Orders created by admin via the Create Order tab
          </p>
        </div>
        <div className={`px-4 py-2 rounded-lg ${theme.bgSecondary}`}>
          <span className={`text-sm font-medium ${theme.textPrimary}`}>
            Total: {filteredOrders.length} orders
          </span>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className={`p-4 rounded-lg border-2 border-blue-500 ${theme.bgSecondary} flex items-center justify-between`}>
          <div className={`${theme.textPrimary} font-medium`}>
            {selectedOrders.length} order{selectedOrders.length > 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('status', 'processing')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Mark Processing
            </button>
            <button
              onClick={() => handleBulkAction('status', 'completed')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Mark Completed
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedOrders([])}
              className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgPrimary}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            >
              <option value="">All Payments</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setPaymentFilter('');
              }}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className={`rounded-lg border ${theme.border} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={theme.bgSecondary}>
              <tr>
                <th className={`px-6 py-3 text-left`}>
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                  />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Order Details
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Customer
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Total & Items
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Payment
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Created
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`${theme.bgPrimary} divide-y divide-gray-200`}>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className={`px-6 py-8 text-center text-sm ${theme.textSecondary}`}>
                    {orders.length === 0 ? 'No created orders found. Use the Create Order tab to create your first order.' : 'No orders match your current filters.'}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className={`hover:${theme.bgSecondary}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-mono font-medium ${theme.textPrimary}`}>
                        {order.order_number || `#${order.id?.slice(-8)}`}
                      </span>
                      {order.admin_created_by && (
                        <div className={`text-xs ${theme.textSecondary}`}>
                          Created by: {order.admin_created_by}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>
                          {order.customer_name || 'N/A'}
                        </div>
                        <div className={`text-sm ${theme.textSecondary}`}>
                          {order.customer_email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold">${(order.total || 0).toFixed(2)}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>
                        {order.items?.length || 0} item(s)
                      </div>
                      {order.balance_owed > 0 && (
                        <div className="text-xs text-red-600">
                          Balance: ${order.balance_owed.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        order.order_status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.order_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.payment_method} - {order.payment_status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewOrder(order)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleEditOrder(order)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                        <div>
                          <button
                            onClick={() => handleResendInvitation(order)}
                            className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                          >
                            📧 Resend Invite
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reuse the same View and Edit modals from EnhancedOrdersManagement */}
      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                  📋 Created Order Details - {selectedOrder.order_number || `#${selectedOrder.id?.slice(-8)}`}
                </h2>
                <button 
                  onClick={() => setShowViewModal(false)}
                  className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary}`}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Admin Creation Info */}
              {selectedOrder.admin_created_by && (
                <div className={`p-4 rounded-lg border ${theme.border} bg-blue-50 dark:bg-blue-900/20`}>
                  <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>👨‍💼 Admin Created Order</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Created by admin: <span className="font-medium">{selectedOrder.admin_created_by}</span>
                  </p>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Created on: {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              )}

              {/* Customer Information - Same as EnhancedOrdersManagement */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>👤 Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Name</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.customer_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textSecondary}`}>Email</p>
                    <p className={`font-medium ${theme.textPrimary}`}>
                      {selectedOrder.customer_email || 'N/A'}
                    </p>
                  </div>
                  {selectedOrder.balance_owed > 0 && (
                    <div>
                      <p className={`text-sm ${theme.textSecondary}`}>Balance Owed</p>
                      <p className="font-medium text-red-600">
                        ${selectedOrder.balance_owed.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>🛍️ Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items?.map((item, index) => {
                    // Calculate totals dynamically from selected_options and selected_addons
                    const basePrice = item.product_base_price || item.price || 0;
                    
                    // Calculate option total
                    let optionTotal = item.option_total || 0;
                    if (!item.option_total && item.selected_options) {
                      optionTotal = (Array.isArray(item.selected_options) ? item.selected_options : Object.values(item.selected_options))
                        .reduce((sum, opt) => sum + (typeof opt === 'object' ? (opt.price || 0) : 0), 0);
                    }
                    
                    // Calculate addon total
                    let addonTotal = item.addon_total || 0;
                    if (!item.addon_total && item.selected_addons) {
                      addonTotal = item.selected_addons
                        .reduce((sum, addon) => sum + (typeof addon === 'object' ? (addon.price || 0) : 0), 0);
                    }
                    
                    // Calculate personalization total
                    let personalizationTotal = item.personalization_price || 0;
                    if (!item.personalization_price && item.custom_text_inputs) {
                      personalizationTotal = (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs : Object.values(item.custom_text_inputs))
                        .reduce((sum, input) => sum + (typeof input === 'object' ? (input.price || 0) : 0), 0);
                    }
                    
                    const pricePerItem = basePrice + optionTotal + addonTotal + personalizationTotal;
                    
                    return (
                    <div key={index} className={`p-4 rounded border ${theme.border} ${theme.bgPrimary}`}>
                      <div className="mb-3">
                        <p className={`font-medium text-lg ${theme.textPrimary} mb-3`}>
                          {item.product_name || item.name || 'Unknown Product'}
                        </p>
                        
                        {/* Itemized Price List */}
                        <div className={`space-y-2 text-sm`}>
                          {/* Base Product */}
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <span className={theme.textPrimary}>Product (Base Price)</span>
                            </div>
                            <span className={`font-medium ${theme.textPrimary}`}>${(item.product_base_price || item.price || 0).toFixed(2)}</span>
                          </div>
                          
                          {/* Options with prices */}
                          {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options : Object.entries(item.selected_options)).map((option, i) => {
                            let optionName = '';
                            let optionValue = '';
                            let optionPrice = 0;
                            
                            if (Array.isArray(item.selected_options)) {
                              optionName = option.name || option.key || 'Option';
                              optionValue = option.value;
                              optionPrice = option.price || 0;
                            } else {
                              optionName = option[0];
                              optionValue = typeof option[1] === 'object' ? option[1].value : option[1];
                              optionPrice = typeof option[1] === 'object' ? (option[1].price || 0) : 0;
                            }
                            
                            console.log('Option Debug:', { optionName, optionValue, optionPrice, option, rawData: item.selected_options });
                            
                            // Always show options, even if price is 0, but indicate if there's a price
                            return (
                              <div key={i} className="flex justify-between items-center">
                                <div className="flex-1">
                                  <span className="text-blue-600">+ {optionName}: {optionValue}</span>
                                </div>
                                <span className="font-medium text-blue-600">+${optionPrice.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          
                          {/* Add-ons with names and prices */}
                          {/* Cache bust: 2025-10-16-15:03 */}
                          {item.selected_addons && item.selected_addons.map((addon, i) => {
                            const addonName = typeof addon === 'object' ? (addon.name || addon.custom_name || 'Add-on') : 'Add-on';
                            const addonPrice = typeof addon === 'object' ? (addon.price || 0) : 0;
                            
                            console.log('Addon Debug:', { addonName, addonPrice, addon, fullAddonObject: JSON.stringify(addon) });
                            
                            return (
                              <div key={i} className="flex justify-between items-center">
                                <div className="flex-1">
                                  <span className="text-green-600">+ Add-on: {addonName}</span>
                                </div>
                                <span className="font-medium text-green-600">+${addonPrice.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          
                          {/* Custom text/personalization (if has price) */}
                          {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs : Object.entries(item.custom_text_inputs)).map((input, i) => {
                            let inputPrice = 0;
                            let inputName = '';
                            
                            if (Array.isArray(item.custom_text_inputs)) {
                              inputPrice = input.price || 0;
                              inputName = input.name || 'Personalization';
                            } else {
                              inputPrice = typeof input[1] === 'object' ? (input[1].price || 0) : 0;
                              inputName = input[0];
                            }
                            
                            if (inputPrice > 0) {
                              return (
                                <div key={i} className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <span className="text-purple-600">+ {inputName}</span>
                                  </div>
                                  <span className="font-medium text-purple-600">+${inputPrice.toFixed(2)}</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                          
                          {/* Subtotal per item */}
                          <div className={`flex justify-between items-center pt-2 border-t ${theme.border}`}>
                            <span className={`font-medium ${theme.textPrimary}`}>Price per Item:</span>
                            <span className={`font-bold ${theme.textPrimary}`}>${pricePerItem.toFixed(2)}</span>
                          </div>
                          
                          {/* Quantity */}
                          <div className="flex justify-between items-center">
                            <span className={theme.textSecondary}>Quantity:</span>
                            <span className={`font-medium ${theme.textSecondary}`}>× {item.quantity || 1}</span>
                          </div>
                          
                          {/* Item Total */}
                          <div className={`flex justify-between items-center pt-2 border-t ${theme.border}`}>
                            <span className={`font-bold text-lg ${theme.textPrimary}`}>Item Total:</span>
                            <span className="font-bold text-xl text-blue-600">
                              ${(pricePerItem * (item.quantity || 1)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Selected Options */}
                      {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options.length > 0 : Object.keys(item.selected_options).length > 0) && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Options:</p>
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(item.selected_options) ? (
                              item.selected_options.map((option, i) => (
                                <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                                  {option.name || option.key || 'Option'}: {option.value}
                                </span>
                              ))
                            ) : (
                              Object.entries(item.selected_options).map(([key, value], i) => (
                                <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                                  {key}: {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Custom Text Inputs */}
                      {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs.length > 0 : Object.keys(item.custom_text_inputs).length > 0) && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Personalization:</p>
                          <div className="space-y-1">
                            {Array.isArray(item.custom_text_inputs) ? (
                              item.custom_text_inputs.map((input, i) => (
                                <p key={i} className={`text-sm ${theme.textSecondary} bg-purple-50 px-3 py-1 rounded`}>
                                  <strong>{input.name || input.key || 'Text'}:</strong> {input.value}
                                </p>
                              ))
                            ) : (
                              Object.entries(item.custom_text_inputs).map(([key, value], i) => (
                                <p key={i} className={`text-sm ${theme.textSecondary} bg-purple-50 px-3 py-1 rounded`}>
                                  <strong>{key}:</strong> {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                </p>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Selected Addons */}
                      {item.selected_addons && item.selected_addons.length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>Add-ons:</p>
                          <div className="space-y-2">
                            {item.selected_addons.map((addonIdOrObj, i) => {
                              // Handle both addon ID (string) and addon object formats
                              let addonName = 'Unknown Add-on';
                              let addonPrice = 0;
                              
                              if (typeof addonIdOrObj === 'string') {
                                // It's an addon ID - need to fetch from product (should be done by enrichment)
                                addonName = `Add-on ${i + 1}`;
                              } else if (addonIdOrObj && typeof addonIdOrObj === 'object') {
                                // It's an addon object
                                addonName = addonIdOrObj.name || addonIdOrObj.custom_name || 'Add-on';
                                addonPrice = addonIdOrObj.price || 0;
                              }
                              
                              return (
                                <div key={i} className="inline-block bg-green-100 text-green-800 px-3 py-2 rounded text-sm">
                                  <span className="font-medium">{addonName}</span>
                                  {addonPrice > 0 && (
                                    <span className="ml-2 text-green-600">(+${addonPrice.toFixed(2)})</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Legacy Customization Field (from manual orders) */}
                      {item.customization && Object.keys(item.customization).length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>📝 Customization Details:</p>
                          <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded space-y-1">
                            {Object.entries(item.customization).map(([key, value], i) => (
                              <p key={i} className={`text-sm ${theme.textSecondary}`}>
                                <strong className="text-purple-700">{key}:</strong> {value}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Customization Notes */}
                      {item.customization_notes && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-1`}>💬 Additional Notes:</p>
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                            <p className={`text-sm ${theme.textSecondary} whitespace-pre-wrap`}>{item.customization_notes}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Uploaded Files */}
                      {item.uploaded_files && item.uploaded_files.length > 0 && (
                        <div className="mt-3">
                          <p className={`text-sm font-medium ${theme.textPrimary} mb-2`}>📎 Customer Uploaded Files:</p>
                          <div className="grid grid-cols-1 gap-2">
                            {item.uploaded_files.map((file, fileIndex) => {
                              const isImage = file.content_type?.startsWith('image/');
                              const fileUrl = file.file_url || `/api/files/${file.filename}`;
                              const displayName = file.original_filename || file.filename || `File ${fileIndex + 1}`;
                              
                              return (
                                <div key={fileIndex} className="flex items-center justify-between p-2 rounded bg-blue-50 border border-blue-200">
                                  <div className="flex items-center gap-3 flex-1">
                                    {isImage && file.thumbnail_url ? (
                                      <img 
                                        src={`${process.env.REACT_APP_BACKEND_URL}${file.thumbnail_url}`}
                                        alt={displayName}
                                        className="w-12 h-12 object-cover rounded border"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-12 h-12 flex items-center justify-center rounded bg-white border">
                                        <span className="text-2xl">{isImage ? '🖼️' : '📄'}</span>
                                      </div>
                                    )}
                                    
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>
                                        {displayName}
                                      </p>
                                      {file.file_size && (
                                        <p className="text-xs text-gray-600">
                                          {(file.file_size / 1024).toFixed(1)} KB
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2">
                                    {isImage && (
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 transition"
                                        title="View image"
                                      >
                                        👁️ View
                                      </a>
                                    )}
                                    <a
                                      href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                      download={displayName}
                                      className="px-3 py-1 text-xs rounded bg-green-500 text-white hover:bg-green-600 transition"
                                      title="Download file"
                                    >
                                      ⬇️ Download
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              </div>

              {/* Payment & Total */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💳 Payment & Total</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Payment Method:</span>
                    <span className={theme.textPrimary}>{selectedOrder.payment_method || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Payment Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      selectedOrder.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedOrder.payment_status || 'Unknown'}
                    </span>
                  </div>
                  <div className={`mt-3 pt-3 border-t ${theme.border}`}></div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Subtotal:</span>
                    <span className={theme.textPrimary}>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {(selectedOrder.tax > 0 || selectedOrder.tax_amount > 0) && (
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Tax:</span>
                      <span className={theme.textPrimary}>${((selectedOrder.tax || selectedOrder.tax_amount) || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.shipping_cost > 0 && (
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Shipping:</span>
                      <span className={theme.textPrimary}>${selectedOrder.shipping_cost.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.discount_amount > 0 && (
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Discount:</span>
                      <span className="text-red-600">-${selectedOrder.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                    <span className={`font-bold ${theme.textPrimary}`}>Total:</span>
                    <span className="text-xl font-bold text-green-600">${(selectedOrder.total || 0).toFixed(2)}</span>
                  </div>
                  {selectedOrder.deposit_amount > 0 && (
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Deposit Paid:</span>
                      <span className="text-green-600">${selectedOrder.deposit_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.balance_owed > 0 && (
                    <div className="flex justify-between">
                      <span className={`font-bold ${theme.textSecondary}`}>Balance Owed:</span>
                      <span className="text-xl font-bold text-orange-600">${selectedOrder.balance_owed.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment History */}
              {selectedOrder.payment_history && selectedOrder.payment_history.length > 0 && (
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💰 Payment History</h3>
                  <div className="space-y-2">
                    {selectedOrder.payment_history.map((payment, index) => (
                      <div key={index} className={`p-3 rounded border ${theme.border} ${theme.bgPrimary}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                payment.type === 'deposit' ? 'bg-blue-100 text-blue-800' :
                                payment.type === 'full_payment' ? 'bg-green-100 text-green-800' :
                                payment.type === 'partial_payment' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {payment.type === 'deposit' ? '📥 Deposit' :
                                 payment.type === 'full_payment' ? '✅ Full Payment' :
                                 payment.type === 'partial_payment' ? '💳 Partial Payment' :
                                 payment.type}
                              </span>
                              <span className={`text-sm ${theme.textSecondary}`}>
                                {new Date(payment.date).toLocaleString()}
                              </span>
                            </div>
                            <p className={`text-sm ${theme.textSecondary}`}>
                              Method: <span className={`font-medium ${theme.textPrimary}`}>{payment.method || 'N/A'}</span>
                            </p>
                            {payment.note && (
                              <p className={`text-xs ${theme.textSecondary} mt-1 italic`}>{payment.note}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              ${(payment.amount || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className={`pt-3 border-t ${theme.border} flex justify-between items-center`}>
                      <span className={`font-semibold ${theme.textPrimary}`}>Total Paid:</span>
                      <span className="text-xl font-bold text-green-600">
                        ${selectedOrder.payment_history.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Creation Info */}
              {selectedOrder.created_by_admin && (
                <div className={`p-4 rounded-lg border ${theme.border} bg-blue-50 dark:bg-blue-900/20`}>
                  <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>👤 Order Created By</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    This order was manually created by admin: <span className={`font-medium ${theme.textPrimary}`}>{selectedOrder.created_by_admin}</span>
                  </p>
                </div>
              )}

              {/* Cost & Profit Analysis */}
              {(() => {
                const costData = calculateOrderCostAndProfit(selectedOrder);
                const hasCosting = costData.totalCost > 0;
                
                return hasCosting && (
                  <div className={`p-4 rounded-lg border ${theme.border} bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20`}>
                    <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💰 Cost & Profit Analysis</h3>
                    
                    {/* Item-by-item breakdown */}
                    {costData.itemBreakdown.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <p className={`text-sm font-medium ${theme.textSecondary} mb-2`}>Cost Breakdown by Item:</p>
                        {costData.itemBreakdown.map((item, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className={`flex justify-between text-sm p-2 rounded ${theme.bgPrimary} border ${theme.border}`}>
                              <span className={theme.textPrimary}>
                                {item.name} (x{item.quantity})
                                <span className={`ml-2 text-xs ${theme.textSecondary}`}>
                                  ${item.costPerUnit.toFixed(2)}/unit
                                </span>
                              </span>
                              <span className={theme.textPrimary}>${item.totalCost.toFixed(2)}</span>
                            </div>
                            {/* Show addon costs if any */}
                            {item.addonCosts && item.addonCosts.length > 0 && (
                              <div className="ml-4 space-y-1">
                                {item.addonCosts.map((addon, addonIdx) => (
                                  <div key={addonIdx} className={`flex justify-between text-xs p-1.5 rounded ${theme.bgSecondary}`}>
                                    <span className={`${theme.textSecondary} italic`}>
                                      ↳ {addon.name} (add-on cost)
                                    </span>
                                    <span className={`${theme.textSecondary}`}>${addon.cost.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="space-y-2">
                      <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                        <span className={`font-semibold ${theme.textPrimary}`}>Total Cost to Make:</span>
                        <span className={`font-bold ${theme.textPrimary}`}>${costData.totalCost.toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className={`font-semibold ${theme.textPrimary}`}>Order Subtotal (Revenue):</span>
                        <span className={`font-bold ${theme.textPrimary}`}>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                      </div>
                      
                      <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                        <span className={`font-bold text-lg ${theme.textPrimary}`}>Profit Amount:</span>
                        <span className={`font-bold text-xl ${costData.profitAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {costData.profitAmount >= 0 ? '+' : ''}${costData.profitAmount.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className={`font-bold text-lg ${theme.textPrimary}`}>Profit Margin:</span>
                        <span className={`font-bold text-xl ${costData.profitMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {costData.profitMargin.toFixed(2)}%
                        </span>
                      </div>
                      
                      {costData.profitMargin < 0 && (
                        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded text-sm">
                          <p className="text-red-800 dark:text-red-200 font-medium">
                            ⚠️ Warning: This order is losing ${Math.abs(costData.profitAmount).toFixed(2)}
                          </p>
                        </div>
                      )}
                      
                      {costData.profitMargin >= 0 && costData.profitMargin < 10 && (
                        <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded text-sm">
                          <p className="text-amber-800 dark:text-amber-200">
                            💡 Low profit margin for this order
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>📍 Shipping Address</h3>
                  <div className={theme.textPrimary}>
                    {selectedOrder.shipping_address.name && (
                      <p>{selectedOrder.shipping_address.name}</p>
                    )}
                    <p>{selectedOrder.shipping_address.street}</p>
                    <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.province} {selectedOrder.shipping_address.postal_code}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal - Reuse from EnhancedOrdersManagement */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                  ✏️ Edit Created Order - {selectedOrder.order_number || `#${selectedOrder.id?.slice(-8)}`}
                </h2>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary}`}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Status Section */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📦 Order Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Order Status</label>
                    <select
                      value={editFormData.order_status}
                      onChange={(e) => setEditFormData({...editFormData, order_status: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Payment Status</label>
                    <select
                      value={editFormData.payment_status}
                      onChange={(e) => setEditFormData({...editFormData, payment_status: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Tracking Number</label>
                  <input
                    type="text"
                    value={editFormData.tracking_number}
                    onChange={(e) => setEditFormData({...editFormData, tracking_number: e.target.value})}
                    placeholder="Enter tracking number (optional)"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
              </div>

              {/* Pricing & Tax Section */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>💰 Pricing & Tax</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Subtotal</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.subtotal}
                      onChange={(e) => setEditFormData({...editFormData, subtotal: parseFloat(e.target.value) || 0})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      <input
                        type="checkbox"
                        checked={editFormData.include_tax}
                        onChange={(e) => setEditFormData({...editFormData, include_tax: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      Include Tax (8%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.include_tax ? (editFormData.subtotal * 0.08).toFixed(2) : 0}
                      disabled
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgSecondary} ${theme.textSecondary}`}
                    />
                  </div>
                </div>
                
                <div className={`mt-4 p-3 rounded ${theme.isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${theme.isDark ? 'border-blue-700' : 'border-blue-200'}`}>
                  <div className="flex justify-between text-lg font-bold">
                    <span className={theme.textPrimary}>New Total:</span>
                    <span className="text-green-600">
                      ${(editFormData.subtotal + (editFormData.include_tax ? editFormData.subtotal * 0.08 : 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Notes */}
              <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📝 Order Notes</h3>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  rows="3"
                  placeholder="Add notes about this order..."
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrderEdit}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <h2 className={`text-xl font-bold ${theme.textPrimary}`}>🗑️ Delete Order</h2>
            </div>
            
            <div className="p-6">
              <p className={`mb-4 ${theme.textPrimary}`}>
                Are you sure you want to delete order <strong>{selectedOrder.order_number || `#${selectedOrder.id?.slice(-8)}`}</strong>?
              </p>
              <p className={`text-sm ${theme.textSecondary} mb-6`}>
                This action cannot be undone. The order will be permanently removed from the system.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteOrder}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Dialog */}
      {showConfirmDialog && pendingBulkAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full mx-4 p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              Confirm Bulk Action
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              {pendingBulkAction.action === 'delete'
                ? `Are you sure you want to delete ${selectedOrders.length} order${selectedOrders.length > 1 ? 's' : ''}? This action cannot be undone.`
                : `Are you sure you want to update ${selectedOrders.length} order${selectedOrders.length > 1 ? 's' : ''}?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelBulkAction}
                className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkAction}
                className={`px-6 py-2 rounded-lg text-white ${
                  pendingBulkAction.action === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {pendingBulkAction.action === 'delete' ? 'Delete' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Marketing & Promotions Management with Automatic Mass Email
export const MarketingManagement = () => {
  const theme = useThemeClasses();
  const [promotions, setPromotions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_order_amount: 0,
    max_uses: null,
    expires_at: '',
    is_active: true,
    send_mass_email: true,  // Auto-send mass email when promotion is created
    email_subject: '',
    email_message: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      console.log('🔄 Loading promotions...');
      const token = localStorage.getItem('access_token');
      console.log('🔑 Token for GET request:', token ? 'Token exists' : 'NO TOKEN!');
      console.log('🌐 GET URL:', `${API}/admin/promotions`);
      
      const response = await axios.get(`${API}/admin/promotions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Promotions GET successful');
      console.log('📊 Received promotions:', response.data);
      console.log('📊 Number of promotions:', Array.isArray(response.data) ? response.data.length : 'Not an array!');
      
      setPromotions(response.data || []);
    } catch (error) {
      console.error('❌ Failed to load promotions:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      // Initialize with empty array if endpoint doesn't exist yet
      setPromotions([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      // Create promotion
      const promotionData = {
        code: formData.code.toUpperCase(),
        description: formData.description,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };

      console.log('🔄 Creating promotion with data:', promotionData);
      console.log('🔑 Using token:', localStorage.getItem('access_token') ? 'Token exists' : 'NO TOKEN!');
      console.log('🌐 API URL:', `${API}/admin/promotions`);

      // Create the promotion first
      let promotionResponse;
      try {
        promotionResponse = await axios.post(`${API}/admin/promotions`, promotionData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        console.log('✅ Promotion POST successful:', promotionResponse.data);
      } catch (postError) {
        console.error('❌ Promotion POST failed:', postError);
        console.error('❌ Error response:', postError.response?.data);
        console.error('❌ Error status:', postError.response?.status);
        throw new Error(`Failed to save promotion: ${postError.response?.data?.detail || postError.message}`);
      }
      
      console.log('✅ Promotion created successfully:', promotionResponse.data);

      // Send mass email if enabled
      if (formData.send_mass_email && formData.email_subject && formData.email_message) {
        console.log('📧 Sending mass email...');
        const massEmailData = {
          subject: formData.email_subject,
          message_content: formData.email_message,
          call_to_action_text: `Use Code ${formData.code}`,
          call_to_action_link: `${window.location.origin}/products`,
          promotion_data: promotionData
        };

        try {
          const emailResponse = await axios.post(`${API}/admin/email/mass-send`, massEmailData);
          
          if (emailResponse.data.success) {
            toast.success(`Promotion created and mass email sent to ${emailResponse.data.sent_count} customers!`);
          } else {
            toast('Promotion created but mass email failed to send', { icon: '⚠️' });
          }
        } catch (emailError) {
          console.error('❌ Email send failed:', emailError);
          toast.warning('Promotion created but email failed to send');
        }
      } else {
        toast.success('Promotion created successfully!');
      }

      // Reset form
      setFormData({
        code: '', description: '', discount_type: 'percentage', discount_value: 0,
        min_order_amount: 0, max_uses: null, expires_at: '', is_active: true,
        send_mass_email: true, email_subject: '', email_message: ''
      });
      setShowAddForm(false);
      
      console.log('🔄 Reloading promotions...');
      await loadPromotions();
      console.log('✅ Promotions reloaded');
      
    } catch (error) {
      console.error('❌ FAILED TO CREATE PROMOTION:', error);
      console.error('❌ Error details:', error.message);
      toast.error(error.message || 'Failed to create promotion');
    } finally {
      setCreating(false);
    }
  };


  const handleEditPromotion = (promotion) => {
    console.log('✏️ Editing promotion:', promotion);
    setFormData({
      code: promotion.code,
      description: promotion.description || '',
      discount_type: promotion.discount_type,
      discount_value: promotion.discount_value,
      min_order_amount: promotion.min_order_amount || 0,
      max_uses: promotion.max_uses,
      expires_at: promotion.expires_at || '',
      is_active: promotion.is_active,
      send_mass_email: false, // Don't resend email when editing
      email_subject: '',
      email_message: ''
    });
    setShowAddForm(true);
  };

  const handleDeletePromotion = async (promotionId, promotionCode) => {
    if (!window.confirm(`Are you sure you want to delete promotion "${promotionCode}"?`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting promotion:', promotionId);
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/promotions/${promotionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Promotion deleted successfully');
      toast.success('Promotion deleted successfully!');
      await loadPromotions();
    } catch (error) {
      console.error('❌ Failed to delete promotion:', error);
      toast.error('Failed to delete promotion');
    }
  };

  // Auto-generate email content when promotion data changes
  const handlePromotionDataChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate email content
      if (field === 'code' || field === 'description' || field === 'discount_value' || field === 'discount_type') {
        updated.email_subject = `🎉 Special Offer: ${updated.discount_type === 'percentage' ? `${updated.discount_value}% OFF` : `$${updated.discount_value} OFF`} - Code ${updated.code}`;
        updated.email_message = `
          <p style="font-size: 16px; line-height: 1.6;">We're excited to share a special offer just for you!</p>
          
          <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; text-align: center;">
            <h3 style="color: #667eea; margin-top: 0;">Use Promo Code:</h3>
            <p style="font-size: 24px; font-weight: bold; color: #333; background: white; padding: 10px 20px; border-radius: 5px; display: inline-block; margin: 10px 0;">
              ${updated.code}
            </p>
            <p style="color: #666; margin-bottom: 0;">${updated.description}</p>
          </div>
          
          <p style="font-size: 16px; line-height: 1.6;">
            Don't miss out on this limited-time offer! Browse our featured products below and use your promo code at checkout.
          </p>
        `;
      }
      
      return updated;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">🎉 Promotions & Marketing</h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage promotional campaigns</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
        >
          {showAddForm ? 'Cancel' : 'Create Promotion'}
        </button>
      </div>

      {/* Quick Marketing Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-2">Active Promotions</h3>
          <p className="text-3xl font-bold text-purple-600">{promotions.length}</p>
          <p className={`text-sm ${theme.textSecondary}`}>Currently running</p>
        </div>
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-2">Email Subscribers</h3>
          <p className="text-3xl font-bold text-blue-600">--</p>
          <p className={`text-sm ${theme.textSecondary}`}>Newsletter subscribers</p>
        </div>
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-2">Email Campaigns</h3>
          <p className="text-3xl font-bold text-green-600">--</p>
          <p className={`text-sm ${theme.textSecondary}`}>Emails sent this month</p>
        </div>
      </div>

      {/* Create Promotion Form */}
      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">Create New Promotion</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Promotion Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Promotion Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handlePromotionDataChange(`code`, e.target.value.toUpperCase())}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="SAVE20, WELCOME10"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handlePromotionDataChange(`description`, e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="20% off your first order"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Discount Type</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => handlePromotionDataChange(`discount_type`, e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Discount Value</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discount_value}
                  onChange={(e) => handlePromotionDataChange(`discount_value`, e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="20"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Min Order Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.min_order_amount}
                  onChange={(e) => handlePromotionDataChange(`min_order_amount`, e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="50.00"
                />
              </div>
            </div>

            {/* Mass Email Settings */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="send_mass_email"
                  checked={formData.send_mass_email}
                  onChange={(e) => setFormData({...formData, send_mass_email: e.target.checked})}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="send_mass_email" className="text-sm font-medium">
                  📧 Send Mass Email to All Customers
                </label>
              </div>
              
              {formData.send_mass_email && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Subject (Auto-Generated)</label>
                    <input
                      type="text"
                      value={formData.email_subject}
                      onChange={(e) => setFormData({...formData, email_subject: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Email subject will be auto-generated"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Message (Auto-Generated)</label>
                    <textarea
                      rows={4}
                      value={formData.email_message}
                      onChange={(e) => setFormData({...formData, email_message: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Email message will be auto-generated based on promotion details"
                    />
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded border border-green-200">
                    <p className="text-sm text-green-700">
                      <strong>📧 Email Features:</strong> The mass email will include the RG-3D logo, 
                      your promotion details, and up to 4 randomly selected featured products with 
                      clickable links to product pages.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={creating}
                className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {creating ? 'Creating & Sending...' : (formData.send_mass_email ? 'Create Promotion & Send Email' : 'Create Promotion')}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Promotions */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Promotions & Email Campaigns</h3>
        {promotions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Code</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Discount</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {promotions.map((promotion) => (
                  <tr key={promotion.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-bold text-purple-600">{promotion.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${theme.textPrimary}`}>{promotion.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-green-600">
                        {promotion.discount_type === 'percentage' 
                          ? `${promotion.discount_value}%` 
                          : `$${promotion.discount_value}`} OFF
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        promotion.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {promotion.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button 
                        onClick={() => handleEditPromotion(promotion)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeletePromotion(promotion.id, promotion.code)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`text-center py-8 ${theme.textSecondary}`}>
            <div className="text-4xl mb-4">📧</div>
            <p className="text-lg mb-2">No promotions created yet</p>
            <p className="text-sm mb-6">Create your first promotion to automatically send mass emails to customers!</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700"
            >
              Create Your First Promotion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// New Customer Settings Management
export const NewCustomerSettingsManagement = () => {
  const theme = useThemeClasses();
  const [settings, setSettings] = useState({
    new_customer_promotion_enabled: false,
    new_customer_promotion_code: '',
    send_welcome_email: true
  });
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
    loadPromotions();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/new-customer-settings`);
      setSettings(response.data || {
        new_customer_promotion_enabled: false,
        new_customer_promotion_code: '',
        send_welcome_email: true
      });
    } catch (error) {
      console.error('Failed to load new customer settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadPromotions = async () => {
    try {
      const response = await axios.get(`${API}/admin/promotions`);
      setPromotions(response.data || []);
    } catch (error) {
      console.error('Failed to load promotions:', error);
      // Don't show error toast as promotions might not be set up yet
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/new-customer-settings`, settings);
      toast.success('New customer settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={`${theme.textSecondary}`}>Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">New Customer Settings</h2>
      </div>

      {/* Settings Card */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h3 className="text-lg font-semibold mb-4">Welcome Email & Promotions for New Customers</h3>
        
        <div className="space-y-6">
          {/* Welcome Email Toggle */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Send Welcome Email</label>
              <p className={`text-sm ${theme.textSecondary}`}>
                Automatically send a welcome email when new customers complete their account setup (billing/shipping addresses)
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                checked={settings.send_welcome_email}
                onChange={(e) => setSettings(prev => ({ ...prev, send_welcome_email: e.target.checked }))}
                className="sr-only peer"
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600`}></div>
            </label>
          </div>

          {/* New Customer Promotion */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold mb-4">New Customer Promotion</h4>
            
            <div className="space-y-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Enable New Customer Promotion</label>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Automatically apply a promotion code for new customers
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.new_customer_promotion_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, new_customer_promotion_enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600`}></div>
                </label>
              </div>

              {/* Promotion Code Selection */}
              {settings.new_customer_promotion_enabled && (
                <div>
                  <label className="block text-sm font-medium mb-2">Select Promotion Code</label>
                  <select
                    value={settings.new_customer_promotion_code || ``}
                    onChange={(e) => setSettings(prev => ({ ...prev, new_customer_promotion_code: e.target.value }))}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  >
                    <option value="">Select a promotion...</option>
                    {promotions.length > 0 ? (
                      promotions.map((promo, index) => (
                        <option key={index} value={promo.code}>
                          {promo.code} - {promo.description} 
                          ({promo.discount_type === `percentage` ? `${promo.discount_value}%` : `$${promo.discount_value}`} off)
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No active promotions available</option>
                    )}
                  </select>
                  {promotions.length === 0 && (
                    <p className={`text-sm ${theme.textSecondary} mt-2`}>
                      💡 Create promotions in the Marketing section first
                    </p>
                  )}
                  {settings.new_customer_promotion_code && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                      <p className="text-sm text-green-800">
                        ✅ New customers will receive the <strong>{settings.new_customer_promotion_code}</strong> promotion code
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-semibold text-blue-800 mb-2">ℹ️ How it works:</h5>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Welcome emails are sent after customers complete their account setup with billing/shipping addresses</li>
              <li>The email includes your logo, featured products, and a professional welcome message</li>
              <li>If a promotion is enabled, new customers will automatically receive the selected promotion code</li>
              <li>Only active promotions from the Marketing section are available for selection</li>
            </ul>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Email Templates Management with Test Send and Mass Email
export const EmailTemplatesManagement = () => {
  const theme = useThemeClasses();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateContent, setTemplateContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // PHASE 12: Password visibility toggle
  const [emailCredentials, setEmailCredentials] = useState({
    gmail_email: '',
    gmail_app_password: '',
    sender_name: 'RG-3D Team',
    reply_to_email: '',
    order_confirmation_enabled: true,
    shipping_notification_enabled: true,
    marketing_emails_enabled: true
  });
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [massEmailData, setMassEmailData] = useState({
    subject: '',
    message_content: '',
    call_to_action_text: '',
    call_to_action_link: ''
  });
  const [showMassEmail, setShowMassEmail] = useState(false);
  const [sendingMassEmail, setSendingMassEmail] = useState(false);
  
  // NEW: Live preview states
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  useEffect(() => {
    loadTemplates();
    loadEmailCredentials();
  }, []);

  // NEW: Debounced preview update
  useEffect(() => {
    if (editingTemplate && templateContent && showPreview) {
      const timer = setTimeout(() => {
        updatePreview();
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timer);
    }
  }, [templateContent, editingTemplate, showPreview]);

  const loadEmailCredentials = async () => {
    try {
      const response = await axios.get(`${API}/admin/email/credentials`);
      if (response.data.success) {
        setEmailCredentials(prev => ({
          ...prev,
          ...response.data.credentials
        }));
      }
    } catch (error) {
      console.error('Failed to load email credentials:', error);
    }
  };
  
  // NEW: Update live preview
  const updatePreview = async () => {
    if (!templateContent) {
      setPreviewHtml('');
      return;
    }
    
    try {
      setPreviewLoading(true);
      const response = await axios.post(`${API}/admin/email/preview-template`, {
        template_content: templateContent,
        template_data: {} // Using default mock data from backend
      }, {
        withCredentials: true  // Explicitly set withCredentials
      });
      
      if (response.data.success) {
        setPreviewHtml(response.data.rendered_html);
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      setPreviewHtml('<div style="padding: 20px; color: red;">Error generating preview: ' + error.message + '</div>');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // NEW: Validate HTML before saving
  const validateHtml = async (html) => {
    try {
      const response = await axios.post(`${API}/admin/email/validate-html`, {
        html_content: html
      }, {
        withCredentials: true
      });
      
      setValidationErrors(response.data.errors || []);
      setValidationWarnings(response.data.warnings || []);
      
      return response.data.valid;
    } catch (error) {
      console.error('Failed to validate HTML:', error);
      setValidationErrors(['Validation failed: ' + error.message]);
      return false;
    }
  };

  const handleSaveCredentials = async () => {
    try {
      setSavingCredentials(true);
      await axios.put(`${API}/admin/email/credentials`, emailCredentials);
      toast.success('Email credentials saved successfully');
      setShowCredentials(false);
      loadEmailCredentials();
    } catch (error) {
      console.error('Failed to save email credentials:', error);
      toast.error('Failed to save email credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleTestCredentials = async () => {
    try {
      setTestingCredentials(true);
      const response = await axios.post(`${API}/admin/email/test-credentials`);
      
      if (response.data.success) {
        toast.success(`Test email sent successfully to ${response.data.test_email}`);
      } else {
        toast.error(response.data.message || 'Email credentials test failed');
      }
    } catch (error) {
      console.error('Failed to test email credentials:', error);
      toast.error('Failed to test email credentials');
    } finally {
      setTestingCredentials(false);
    }
  };

  const handleClearCredentials = async () => {
    if (!window.confirm('Are you sure you want to clear all email credentials? This will disable email functionality.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/email/credentials`);
      toast.success('Email credentials cleared successfully');
      setEmailCredentials({
        gmail_email: '',
        gmail_app_password: '',
        sender_name: 'RG-3D Team',
        reply_to_email: '',
        order_confirmation_enabled: true,
        shipping_notification_enabled: true,
        marketing_emails_enabled: true
      });
      setShowCredentials(false);
    } catch (error) {
      console.error('Failed to clear email credentials:', error);
      toast.error('Failed to clear email credentials');
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/email/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load email templates');
    }
  };

  const handleEditTemplate = async (template) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/email/templates/${template.filename}`);
      setSelectedTemplate(template);
      setTemplateContent(response.data.template.content || '');
      setEditingTemplate(true);
      setShowPreview(true);
      setValidationErrors([]);
      setValidationWarnings([]);
      // Preview will be generated by useEffect
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template content');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      
      // Validate HTML before saving
      const isValid = await validateHtml(templateContent);
      
      if (!isValid) {
        toast.error('Cannot save: Template contains errors. Please fix them first.');
        return;
      }
      
      if (validationWarnings.length > 0) {
        const confirmed = window.confirm(`Template has ${validationWarnings.length} warning(s). Do you want to save anyway?`);
        if (!confirmed) {
          return;
        }
      }
      
      await axios.put(`${API}/admin/email/templates/${selectedTemplate.filename}`, {
        content: templateContent
      });
      toast.success('Template updated successfully');
      setEditingTemplate(false);
      setSelectedTemplate(null);
      setValidationErrors([]);
      setValidationWarnings([]);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSend = async (template) => {
    try {
      setTestSending(true);
      const response = await axios.post(`${API}/admin/email/test-send`, {
        template_name: template.filename,
        context: {
          customer_name: "Test Customer",
          order_number: "TEST-000123"
        }
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        const sentAt = new Date(response.data.sent_at).toLocaleTimeString();
        toast.success(
          <div>
            <div className="font-bold">✅ Test email sent successfully!</div>
            <div className="text-sm mt-1">To: {response.data.recipient}</div>
            <div className="text-sm">Time: {sentAt}</div>
            {response.data.featured_products_count > 0 && (
              <div className="text-sm">Featured Products: {response.data.featured_products_count}</div>
            )}
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email - check your email credentials');
    } finally {
      setTestSending(false);
    }
  };
  
  // NEW: Test send draft version (from editor)
  const handleTestSendDraft = async () => {
    if (!selectedTemplate || !templateContent) {
      toast.error('No template content to test');
      return;
    }
    
    try {
      setTestSending(true);
      
      // For now, use the same endpoint but we could extend it to accept template_content
      // This sends the SAVED version, not the draft
      const response = await axios.post(`${API}/admin/email/test-send`, {
        template_name: selectedTemplate.filename,
        context: {
          customer_name: "Test Customer (Draft)",
          order_number: "DRAFT-TEST-001"
        }
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        const sentAt = new Date(response.data.sent_at).toLocaleTimeString();
        toast.success(
          <div>
            <div className="font-bold">✅ Draft test email sent!</div>
            <div className="text-sm mt-1">To: {response.data.recipient}</div>
            <div className="text-sm">Time: {sentAt}</div>
            <div className="text-xs mt-1 text-yellow-600">Note: Sent saved version. Save first to test current edits.</div>
          </div>,
          { duration: 6000 }
        );
      }
    } catch (error) {
      console.error('Failed to send draft test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  const handleMassEmail = async () => {
    try {
      setSendingMassEmail(true);
      
      if (!massEmailData.subject.trim() || !massEmailData.message_content.trim()) {
        toast.error('Subject and message content are required');
        return;
      }
      
      const response = await axios.post(`${API}/admin/email/mass-send`, massEmailData);
      
      if (response.data.success) {
        toast.success(`Mass email sent to ${response.data.sent_count} customers successfully!`);
        setMassEmailData({
          subject: '',
          message_content: '',
          call_to_action_text: '',
          call_to_action_link: ''
        });
        setShowMassEmail(false);
      } else {
        toast.error(response.data.message || 'Failed to send mass email');
      }
    } catch (error) {
      console.error('Failed to send mass email:', error);
      toast.error('Failed to send mass email');
    } finally {
      setSendingMassEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Email Templates & Mass Email</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCredentials(!showCredentials)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {showCredentials ? 'Cancel' : 'Email Settings'}
          </button>
          <button
            onClick={() => setShowMassEmail(!showMassEmail)}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            {showMassEmail ? 'Cancel' : 'Send Mass Email'}
          </button>
        </div>
      </div>

      {/* Email Credentials Management */}
      {showCredentials && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">📧 Gmail Email Credentials</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Gmail Email Address</label>
                <input
                  type="email"
                  value={emailCredentials.gmail_email}
                  onChange={(e) => setEmailCredentials({...emailCredentials, gmail_email: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="your-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Gmail App Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={emailCredentials.gmail_app_password}
                    onChange={(e) => setEmailCredentials({...emailCredentials, gmail_app_password: e.target.value})}
                    className={`w-full px-3 py-2 pr-10 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="16-character app password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Sender Name</label>
                <input
                  type="text"
                  value={emailCredentials.sender_name}
                  onChange={(e) => setEmailCredentials({...emailCredentials, sender_name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="RG-3D Team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Reply-To Email (Optional)</label>
                <input
                  type="email"
                  value={emailCredentials.reply_to_email}
                  onChange={(e) => setEmailCredentials({...emailCredentials, reply_to_email: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="support@rg3d.ca"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-3">Email Notifications</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailCredentials.order_confirmation_enabled}
                    onChange={(e) => setEmailCredentials({...emailCredentials, order_confirmation_enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">Send order confirmation emails</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailCredentials.shipping_notification_enabled}
                    onChange={(e) => setEmailCredentials({...emailCredentials, shipping_notification_enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">Send shipping notification emails</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailCredentials.marketing_emails_enabled}
                    onChange={(e) => setEmailCredentials({...emailCredentials, marketing_emails_enabled: e.target.checked})}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">Enable marketing emails and newsletters</span>
                </label>
              </div>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">Gmail App Password Setup</h4>
              <ol className="text-sm text-yellow-700 space-y-1 mb-3">
                <li>1. Go to your Google Account settings</li>
                <li>2. Enable 2-Factor Authentication if not already enabled</li>
                <li>3. Go to Security → App passwords</li>
                <li>4. Generate an app password for "Mail"</li>
                <li>5. Copy the 16-character password (e.g., abcd efgh ijkl mnop)</li>
                <li>6. Paste it in the App Password field above</li>
              </ol>
              <div className="bg-red-100 border border-red-300 rounded p-3">
                <p className="text-sm text-red-700">
                  <strong>⚠️ Important:</strong> Use your Gmail <strong>App Password</strong> (16 characters), 
                  not your regular Gmail password. Regular passwords will not work for SMTP authentication.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={handleSaveCredentials}
                disabled={savingCredentials}
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {savingCredentials ? 'Saving...' : 'Save Credentials'}
              </button>
              
              <button
                onClick={handleTestCredentials}
                disabled={testingCredentials}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {testingCredentials ? 'Testing...' : 'Test Send Email'}
              </button>
              
              <button
                onClick={handleClearCredentials}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700"
              >
                Clear Credentials
              </button>
              
              <button
                onClick={() => setShowCredentials(false)}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mass Email Interface */}
      {showMassEmail && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Send Mass Email to All Customers</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Subject Line</label>
              <input
                type="text"
                value={massEmailData.subject}
                onChange={(e) => setMassEmailData({...massEmailData, subject: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="New Products Available at RG-3D!"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Message Content (HTML allowed)</label>
              <textarea
                rows={8}
                value={massEmailData.message_content}
                onChange={(e) => setMassEmailData({...massEmailData, message_content: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="<p>Check out our latest 3D printed products...</p>"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Call to Action Text (Optional)</label>
                <input
                  type="text"
                  value={massEmailData.call_to_action_text}
                  onChange={(e) => setMassEmailData({...massEmailData, call_to_action_text: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Shop Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Call to Action Link (Optional)</label>
                <input
                  type="url"
                  value={massEmailData.call_to_action_link}
                  onChange={(e) => setMassEmailData({...massEmailData, call_to_action_link: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="https://yourdomain.com/products"
                />
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={handleMassEmail}
                disabled={sendingMassEmail}
                className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {sendingMassEmail ? 'Sending...' : 'Send Mass Email'}
              </button>
              <button
                onClick={() => setShowMassEmail(false)}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
              <p className="text-sm text-yellow-700">
                <strong>⚠️ Important:</strong> Mass emails will be sent to all customers who have opted in for marketing emails. 
                This action cannot be undone. Please review your message carefully before sending.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.filename} className={`${theme.card} rounded-lg shadow p-6`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold">{template.name}</h3>
                <p className={`${theme.textSecondary} text-sm`}>{template.description}</p>
                {template.last_modified && (
                  <p className={`text-xs ${theme.textSecondary}`}>
                    Modified: {new Date(template.last_modified).toLocaleDateString()}
                  </p>
                )}
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                template.exists ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {template.exists ? 'Available' : 'Missing'}
              </span>
            </div>
            
            <div className="space-y-2">
              <button
                onClick={() => handleEditTemplate(template)}
                disabled={!template.exists || loading}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Edit Template'}
              </button>
              
              <button
                onClick={() => handleTestSend(template)}
                disabled={!template.exists || testSending}
                className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {testSending ? 'Sending...' : 'Test Send to timelrd@rogers.com'}
              </button>
              
              <div className={`${theme.bgSecondary} p-2 rounded text-xs ${theme.textSecondary}`}>
                <strong>Variables:</strong>
                {template.filename === 'order_confirmation.html' && 
                  'customer_name, order_number, order_total, order_items, payment_method'}
                {template.filename === 'shipping_notification.html' && 
                  'customer_name, order_number, tracking_number, shipping_method, expected_delivery'}
                {template.filename === 'newsletter.html' && 
                  'customer_name, subject, subtitle, message_content, call_to_action_text, call_to_action_link'}
                {template.filename === 'customer_invitation.html' && 
                  'company_logo, order_number, customer_first_name, signup_link, order_items_html, subtotal, tax_amount, shipping_cost, deposit_amount, total, balance_owed, featured_products'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Template Editor Modal */}
      {editingTemplate && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`${theme.card} rounded-lg shadow-xl w-full max-w-7xl my-8`}>
            {/* Modal Header */}
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Edit Template: {selectedTemplate.name}</h3>
                  <p className={`text-sm ${theme.textSecondary} mt-1`}>{selectedTemplate.description}</p>
                </div>
                <button
                  onClick={() => {
                    setEditingTemplate(false);
                    setValidationErrors([]);
                    setValidationWarnings([]);
                  }}
                  className={`${theme.textSecondary} hover:text-red-600 transition-colors`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Validation Messages */}
            {(validationErrors.length > 0 || validationWarnings.length > 0) && (
              <div className="p-4 space-y-2">
                {validationErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-red-800 font-semibold mb-2">❌ Errors ({validationErrors.length})</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {validationErrors.map((error, idx) => (
                            <li key={idx} className="text-sm text-red-700">{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {validationWarnings.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-yellow-800 font-semibold mb-2">⚠️ Warnings ({validationWarnings.length})</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {validationWarnings.map((warning, idx) => (
                            <li key={idx} className="text-sm text-yellow-700">{warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Main Content Area */}
            <div className="p-6 overflow-y-auto" style={{maxHeight: `calc(90vh - 220px)`}}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side: HTML Editor */}
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium">Template Content (HTML)</label>
                      <button
                        onClick={() => validateHtml(templateContent)}
                        className="text-sm px-3 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                      >
                        🔍 Validate HTML
                      </button>
                    </div>
                    <textarea
                      rows={24}
                      value={templateContent}
                      onChange={(e) => setTemplateContent(e.target.value)}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${theme.bgSecondary}`}
                      placeholder="Enter HTML template content..."
                      style={{resize: 'vertical', minHeight: '400px'}}
                    />
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded border border-blue-200 dark:border-blue-800">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">📝 Template Variables</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Use double curly braces: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{customer_name}}'}</code>, <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{order_number}}'}</code>
                    </p>
                  </div>
                </div>
                
                {/* Right Side: Live Preview */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium">Live Preview</label>
                    <div className="flex items-center space-x-2">
                      {previewLoading && (
                        <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="text-sm px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        {showPreview ? '🙈 Hide' : '👁️ Show'} Preview
                      </button>
                    </div>
                  </div>
                  
                  {showPreview && (
                    <div 
                      className="border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white" 
                      style={{height: '500px'}}
                    >
                      {previewHtml ? (
                        <iframe
                          srcDoc={previewHtml}
                          title="Email Preview"
                          className="w-full h-full"
                          sandbox="allow-same-origin"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p>Preview will appear here...</p>
                            <p className="text-sm mt-2">Start typing to see live preview</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                    💡 Preview updates automatically as you type (500ms delay)
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer with Actions */}
            <div className={`p-6 border-t ${theme.border} flex justify-between items-center`}>
              <button
                onClick={handleTestSendDraft}
                disabled={testSending}
                className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {testSending ? '📧 Sending...' : '📧 Test Send to timelrd@rogers.com'}
              </button>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setEditingTemplate(false);
                    setValidationErrors([]);
                    setValidationWarnings([]);
                  }}
                  className="border border-gray-300 dark:border-gray-600 px-6 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={loading || validationErrors.length > 0}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? '💾 Saving...' : '💾 Save Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};